import React, { useMemo, useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import EditIcon from '@mui/icons-material/Edit';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SendIcon from '@mui/icons-material/Send';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Plot from 'react-plotly.js';
import {
  generateDemoData, SaleRow,
  generateProductCatalog, ProductCatalogRow,
  generateRegionTargets, RegionTargetRow,
} from '../utils/demoData';
import { formatCell, detectColumnType } from '../utils/formatters';

type Tab = 'explore' | 'clean' | 'filter' | 'join' | 'add' | 'chart' | 'ai';
interface Props { onSignUp: () => void; onBack: () => void; }

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'explore', label: 'Overview', icon: <StorageIcon sx={{ fontSize: 15 }} /> },
  { id: 'clean',   label: 'Clean',   icon: <CleaningServicesIcon sx={{ fontSize: 15 }} /> },
  { id: 'join',    label: 'Join',    icon: <MergeTypeIcon sx={{ fontSize: 15 }} /> },
  { id: 'filter',  label: 'Filter',  icon: <EditIcon sx={{ fontSize: 15 }} /> },
  { id: 'add',     label: 'Add',     icon: <AddCircleOutlineIcon sx={{ fontSize: 15 }} /> },
  { id: 'chart',   label: 'Chart',   icon: <BarChartIcon sx={{ fontSize: 15 }} /> },
  { id: 'ai',      label: 'AI',      icon: <AutoAwesomeIcon sx={{ fontSize: 15 }} /> },
];

// ── Clean Tab: AI-generated column recommendations ───────────────────────────
interface CleanRec {
  col: string;
  status: 'warning' | 'good';
  issue: string;
  recommendation: string;
  action: string;
  sampleValues: string[];
}

const DEMO_CLEAN_RECS: CleanRec[] = [
  {
    col: 'profit_margin',
    status: 'warning',
    issue: 'Stored as decimal but represents a percentage',
    recommendation: 'Values like 0.31 mean 31%. Recommend displaying as % so users aren\'t confused by decimals.',
    action: 'Format as %',
    sampleValues: ['0.31', '0.16', '0.28', '0.24', '0.19'],
  },
  {
    col: 'discount',
    status: 'warning',
    issue: 'Integer values represent a percentage but display as plain numbers',
    recommendation: 'Values like 20 mean 20% discount. Recommend adding % suffix so it reads clearly in charts and filters.',
    action: 'Format as %',
    sampleValues: ['20', '10', '15', '5', '0'],
  },
  {
    col: 'region',
    status: 'warning',
    issue: 'Potential whitespace inconsistencies detected',
    recommendation: 'Values like "North " (with trailing space) and "North" look the same but break filters and joins. Recommend trimming whitespace and standardising capitalisation.',
    action: 'Trim & standardise',
    sampleValues: ['North', 'South', 'East', 'West ', 'Central'],
  },
  {
    col: 'date',
    status: 'good',
    issue: '',
    recommendation: 'All 1,000 dates are valid and consistently formatted as YYYY-MM-DD. No action needed.',
    action: '',
    sampleValues: ['2024-01-09', '2024-03-15', '2024-07-22', '2024-11-01', '2024-06-30'],
  },
  {
    col: 'sales',
    status: 'good',
    issue: '',
    recommendation: 'Numeric currency values with no blanks or outliers detected. Already displaying correctly as $.',
    action: '',
    sampleValues: ['$528', '$4,655', '$13,707', '$74', '$931'],
  },
  {
    col: 'product',
    status: 'good',
    issue: '',
    recommendation: '10 unique product names, consistent capitalisation, no blanks. No action needed.',
    action: '',
    sampleValues: ['Laptop', 'Keyboard', 'Headphones', 'Mouse', 'Camera'],
  },
  {
    col: 'customer_type',
    status: 'good',
    issue: '',
    recommendation: '5 unique values, consistently capitalised across all 1,000 rows. No action needed.',
    action: '',
    sampleValues: ['Business', 'Individual', 'Education', 'Government', 'Healthcare'],
  },
  {
    col: 'payment_method',
    status: 'good',
    issue: '',
    recommendation: '5 unique payment methods, all formatted consistently. No action needed.',
    action: '',
    sampleValues: ['Credit Card', 'Debit Card', 'Cash', 'Bank Transfer', 'Digital Wallet'],
  },
  {
    col: 'quantity',
    status: 'good',
    issue: '',
    recommendation: 'Integer values ranging 1–10, no blanks or negative values detected.',
    action: '',
    sampleValues: ['6', '7', '9', '1', '2'],
  },
  {
    col: 'category',
    status: 'good',
    issue: '',
    recommendation: '4 unique categories — Computers, Audio, Mobile, Accessories, Imaging. All correctly formatted.',
    action: '',
    sampleValues: ['Accessories', 'Imaging', 'Computers', 'Mobile', 'Audio'],
  },
];

const REGIONAL_TARGETS_CLEAN_RECS: CleanRec[] = [
  {
    col: 'sales_target_2024',
    status: 'warning',
    issue: 'Large numbers stored as plain integers',
    recommendation: 'Values like 500000 are hard to read at a glance. Recommend formatting as $500K so the scale is immediately clear in charts and reports.',
    action: 'Format as $K',
    sampleValues: ['500000', '420000', '380000', '450000', '290000'],
  },
  {
    col: 'territory_manager',
    status: 'warning',
    issue: 'Full names — could be split into First & Last name',
    recommendation: 'Values like "Alice Chen" contain first and last name in one column. If you plan to sort or filter by last name, recommend splitting into two separate columns.',
    action: 'Split into First + Last name',
    sampleValues: ['Alice Chen', 'Bob Martinez', 'Carol Singh', 'David Park', 'Eve Johnson'],
  },
  {
    col: 'region',
    status: 'good',
    issue: '',
    recommendation: '9 unique region names, consistent capitalisation, no blanks. No action needed.',
    action: '',
    sampleValues: ['North', 'South', 'East', 'West', 'Central'],
  },
  {
    col: 'sales_rep_count',
    status: 'good',
    issue: '',
    recommendation: 'Integer values ranging 7–12, no blanks or anomalies detected.',
    action: '',
    sampleValues: ['12', '10', '9', '11', '7'],
  },
  {
    col: 'market_size',
    status: 'good',
    issue: '',
    recommendation: '3 unique values — Large, Medium, Small. Consistently formatted across all 9 rows.',
    action: '',
    sampleValues: ['Large', 'Large', 'Medium', 'Large', 'Medium'],
  },
];

const PRODUCT_CATALOG_CLEAN_RECS: CleanRec[] = [
  {
    col: 'avg_rating',
    status: 'warning',
    issue: 'Rating shown as plain decimal without context',
    recommendation: 'Values like 4.6 are ambiguous — is it out of 5 or 10? Recommend displaying as "4.6 / 5" so it reads clearly in any table or chart.',
    action: 'Display as X / 5',
    sampleValues: ['4.6', '4.3', '4.5', '4.7', '4.8'],
  },
  {
    col: 'warranty_years',
    status: 'warning',
    issue: 'Number without unit — unclear what it represents',
    recommendation: 'Values like 2 could mean months or years. Recommend adding "yr" suffix (e.g. "2 yr") so it reads unambiguously.',
    action: 'Add "yr" suffix',
    sampleValues: ['2', '1', '1', '3', '2'],
  },
  {
    col: 'product',
    status: 'good',
    issue: '',
    recommendation: '10 unique product names, consistent capitalisation. No action needed.',
    action: '',
    sampleValues: ['Laptop', 'Mouse', 'Keyboard', 'Monitor', 'Headphones'],
  },
  {
    col: 'supplier',
    status: 'good',
    issue: '',
    recommendation: '6 unique supplier names, all consistently formatted.',
    action: '',
    sampleValues: ['TechCorp Ltd', 'PeriphX Inc', 'DisplayPro', 'AudioWave', 'LensMaster'],
  },
  {
    col: 'country_of_origin',
    status: 'good',
    issue: '',
    recommendation: '5 unique countries, no abbreviations or inconsistencies detected.',
    action: '',
    sampleValues: ['Taiwan', 'China', 'South Korea', 'Germany', 'Japan'],
  },
  {
    col: 'launch_year',
    status: 'good',
    issue: '',
    recommendation: 'Integer years ranging 2020–2023, all valid. No action needed.',
    action: '',
    sampleValues: ['2022', '2023', '2022', '2021', '2023'],
  },
  {
    col: 'in_stock',
    status: 'good',
    issue: '',
    recommendation: '"Yes" / "No" values, consistently formatted across all 10 rows.',
    action: '',
    sampleValues: ['Yes', 'Yes', 'Yes', 'Yes', 'No'],
  },
];

const PAGE_SIZE = 50;
const NUM_COLS: (keyof SaleRow)[] = ['sales', 'quantity', 'profit_margin', 'discount'];
const CAT_COLS: (keyof SaleRow)[] = ['product', 'region', 'category', 'customer_type', 'payment_method'];

// ─── Proactive insight card ──────────────────────────────────────────────────
const InsightCard: React.FC<{ icon: React.ReactNode; title: string; value: string; sub: string; color: string }> =
  ({ icon, title, value, sub, color }) => (
  <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, bgcolor: '#fff', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>{title}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', lineHeight: 1.1, letterSpacing: '-0.03em' }} noWrap>{value}</Typography>
      <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.25 }}>{sub}</Typography>
    </Box>
  </Box>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const DemoDatasetPage: React.FC<Props> = ({ onSignUp, onBack }) => {
  const allData = useMemo(() => generateDemoData(1000), []);
  const [tab, setTab] = useState<Tab>('explore');

  // Explore
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // Filter
  const [filterDataset,  setFilterDataset]  = useState<DS>('sales');
  const [filterCol,      setFilterCol]      = useState<string>('region');
  const [filterOp,       setFilterOp]       = useState('is');
  const [filterVal,      setFilterVal]      = useState('North');
  // Per-dataset active filters (Filter tab only — independent from global activeFilters)
  const [filtersByDS,    setFiltersByDS]    = useState<Partial<Record<DS, { col: string; op: string; val: string }[]>>>({});
  const [filterSavedMap, setFilterSavedMap] = useState<Partial<Record<DS, boolean>>>({});
  // Global filters (still used by Overview search chip / Chart / AI)
  const [activeFilters, setActiveFilters] = useState<{ col: keyof SaleRow; op: string; val: string }[]>([]);

  // Add column (plain English)
  const [addDataset,    setAddDataset]    = useState<DS>('sales');
  const [addSavedMap,   setAddSavedMap]   = useState<Partial<Record<DS, boolean>>>({});
  const [addStep, setAddStep] = useState(0);
  const [labelColName, setLabelColName] = useState('');
  const [labelWhenCol, setLabelWhenCol] = useState<string>('sales');
  const [labelCondition, setLabelCondition] = useState('is greater than');
  const [labelThreshold, setLabelThreshold] = useState('5000');
  const [labelIfTrue, setLabelIfTrue] = useState('High');
  const [labelIfFalse, setLabelIfFalse] = useState('Low');
  const [addedLabels, setAddedLabels] = useState<{ name: string; col: string; op: string; thresh: string; t: string; f: string }[]>([]);

  // Chart
  const [chartDataset, setChartDataset] = useState<string>('sales');
  const [chartSaved,   setChartSaved]   = useState(false);
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'scatter'>('bar');
  const [barOrientation, setBarOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const [xAxis, setXAxis] = useState<string>('product');
  const [yAxis, setYAxis] = useState<string>('sales');
  const [aggFunc, setAggFunc] = useState<'sum' | 'count' | 'average' | 'max' | 'min'>('sum');

  // AI
  const [aiDataset, setAiDataset] = useState<string>('sales');
  const [aiSaved,   setAiSaved]   = useState(false);

  // AI chat
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState<{ role: 'user' | 'ai'; text: string }[]>([
    { role: 'ai', text: "Hi! Ask me anything about your sales data in plain English — like \"What's the top product?\" or \"Which region has the most revenue?\"" },
  ]);

  // Dataset types
  type DS    = 'sales' | 'regional' | 'products';
  type TabDS = 'sales' | 'sales_cleaned' | 'regional' | 'regional_cleaned' | 'products' | 'products_cleaned';

  const DS_META: Record<TabDS, { label: string; baseDS: DS; cleaned: boolean }> = {
    sales:              { label: 'sales_2024.csv',             baseDS: 'sales',    cleaned: false },
    sales_cleaned:      { label: 'sales_2024 — Cleaned',       baseDS: 'sales',    cleaned: true  },
    regional:           { label: 'Regional Targets',           baseDS: 'regional', cleaned: false },
    regional_cleaned:   { label: 'Regional Targets — Cleaned', baseDS: 'regional', cleaned: true  },
    products:           { label: 'Product Catalog',            baseDS: 'products', cleaned: false },
    products_cleaned:   { label: 'Product Catalog — Cleaned',  baseDS: 'products', cleaned: true  },
  };

  // tabDatasets = what's checked in dropdown = what shows as tabs (string so joined ids fit too)
  const [tabDatasets, setTabDatasets] = useState<string[]>(['sales', 'regional', 'products']);

  // Join
  const productCatalog = useMemo(() => generateProductCatalog(), []);
  const regionTargets  = useMemo(() => generateRegionTargets(),  []);
  const [joinLeft,  setJoinLeft]  = useState<TabDS>('sales');
  const [joinRight, setJoinRight] = useState<TabDS>('products');
  const [joinDone,  setJoinDone]  = useState(false);
  const [joinSaved, setJoinSaved] = useState(false);
  const [joinPage,  setJoinPage]  = useState(0);
  const [joinRadio, setJoinRadio] = useState<'left' | 'right'>('left');

  const JOIN_PAGE_SIZE = 20;

  // Helper: resolve base DS from any TabDS (cleaned variants use same rows/cols)
  const getBaseDS = (ds: TabDS): DS => DS_META[ds].baseDS;

  // Helper: get rows for any TabDS (cleaned = same underlying rows)
  const getDSRows = (ds: TabDS): any[] => {
    const base = getBaseDS(ds);
    return base === 'sales' ? allData : base === 'regional' ? regionTargets : productCatalog;
  };
  const getDSCols = (ds: TabDS): string[] => {
    const base = getBaseDS(ds);
    return base === 'sales' ? (Object.keys(allData[0]) as string[])
      : base === 'regional' ? Object.keys(regionTargets[0])
      : Object.keys(productCatalog[0]);
  };

  // Auto-detect join key between two datasets (shared column name)
  const detectJoinKey = (left: TabDS, right: TabDS): string | null => {
    const leftCols  = new Set(getDSCols(left));
    const rightCols = getDSCols(right);
    return rightCols.find(c => leftCols.has(c)) ?? null;
  };

  const joinKey = joinLeft !== joinRight ? detectJoinKey(joinLeft, joinRight) : null;

  const joinedData = useMemo<any[]>(() => {
    if (!joinDone || !joinKey || joinLeft === joinRight) return [];
    const leftRows  = getDSRows(joinLeft);
    const rightRows = getDSRows(joinRight);
    const rightCols = getDSCols(joinRight).filter(c => c !== joinKey);
    // Build lookup from right table
    const rightMap: Record<string, any> = {};
    rightRows.forEach((r: any) => { rightMap[String(r[joinKey])] = r; });
    return leftRows.map((row: any) => {
      const match = rightMap[String(row[joinKey])];
      const extra = Object.fromEntries(rightCols.map(c => [c, match ? match[c] : '—']));
      return { ...row, ...extra };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinDone, joinLeft, joinRight, joinKey]);

  const joinTotalPages = Math.ceil(joinedData.length / JOIN_PAGE_SIZE);
  const joinPageData   = joinedData.slice(joinPage * JOIN_PAGE_SIZE, (joinPage + 1) * JOIN_PAGE_SIZE);

  // Clean tab state
  const [cleanAccepted, setCleanAccepted] = useState<Set<string>>(new Set());
  const [cleanSkipped,  setCleanSkipped]  = useState<Set<string>>(new Set());
  // Per-dataset saved tracking
  // Saved joined datasets (dynamic — user can save multiple joins)
  interface SavedJoin { id: string; label: string; rows: any[]; cols: string[] }
  const [savedJoins, setSavedJoins] = useState<SavedJoin[]>([]);

  const [cleanSavedMap,    setCleanSavedMap]    = useState<Partial<Record<DS, boolean>>>({});
  const [savedAcceptedMap, setSavedAcceptedMap] = useState<Partial<Record<DS, Set<string>>>>({});
  const [activeTab, setActiveTab] = useState<string>('sales');
  const [cleanScanned,  setCleanScanned]  = useState(false);

  const [cleanDataset, setCleanDataset] = useState<DS>('sales');
  const [overrideExpanded, setOverrideExpanded] = useState<string | null>(null);
  const [overrideAction,   setOverrideAction]   = useState<Record<string, string>>({});
  const [overrideAccepted, setOverrideAccepted] = useState<Set<string>>(new Set());

  // ── Tab metadata helpers (handles both fixed TabDS and dynamic joined ids) ──
  const getTabLabel = (tab: string): string => {
    if (tab in DS_META) return DS_META[tab as TabDS].label;
    return savedJoins.find(j => j.id === tab)?.label ?? tab;
  };
  const isTabCleaned  = (tab: string): boolean => (tab in DS_META) && DS_META[tab as TabDS].cleaned;
  const isTabJoined   = (tab: string): boolean => !(tab in DS_META);
  const getTabBaseDS  = (tab: string): DS | null => (tab in DS_META) ? DS_META[tab as TabDS].baseDS : null;
  const getTabRows    = (tab: string): any[] => {
    if (tab in DS_META) {
      const base = DS_META[tab as TabDS].baseDS;
      return base === 'sales' ? filteredData : base === 'regional' ? regionTargets : productCatalog;
    }
    return savedJoins.find(j => j.id === tab)?.rows ?? [];
  };
  const getTabCols    = (tab: string): string[] => {
    if (tab in DS_META) {
      const base = DS_META[tab as TabDS].baseDS;
      return base === 'sales' ? (columns as string[]) : base === 'regional' ? Object.keys(regionTargets[0]) : Object.keys(productCatalog[0]);
    }
    return savedJoins.find(j => j.id === tab)?.cols ?? [];
  };

  // All datasets available to appear in the dropdown
  const allAvailableDatasets: string[] = [
    'sales',
    ...(cleanSavedMap.sales    ? ['sales_cleaned'    as TabDS] : []),
    'regional',
    ...(cleanSavedMap.regional ? ['regional_cleaned' as TabDS] : []),
    'products',
    ...(cleanSavedMap.products ? ['products_cleaned' as TabDS] : []),
    ...savedJoins.map(j => j.id),
  ];

  // Derived clean state
  const cleanSaved        = !!cleanSavedMap[cleanDataset];
  const viewingCleaned    = isTabCleaned(activeTab);
  const viewingCleanedDS  = viewingCleaned ? getTabBaseDS(activeTab) : null;
  const activeCleanedAccepted: Set<string> = viewingCleanedDS ? (savedAcceptedMap[viewingCleanedDS] ?? new Set()) : new Set();

  const activeCleanRecs = cleanDataset === 'sales' ? DEMO_CLEAN_RECS
    : cleanDataset === 'regional' ? REGIONAL_TARGETS_CLEAN_RECS
    : PRODUCT_CATALOG_CLEAN_RECS;
  const cleanDatasetLabel = cleanDataset === 'sales' ? 'sales_2024.csv'
    : cleanDataset === 'regional' ? 'Regional Targets'
    : 'Product Catalog';
  const cleanWarnings      = activeCleanRecs.filter(r => r.status === 'warning');
  const cleanIssueCount    = cleanWarnings.length;
  const cleanAcceptedCount = cleanWarnings.filter(r => cleanAccepted.has(r.col)).length;

  const OVERRIDE_OPTIONS: Record<string, { label: string; before: string[]; after: (v: string) => string }[]> = {
    date:           [
      { label: 'Display as Jan 9, 2024',  before: ['2024-01-09','2024-03-15','2024-07-22'], after: v => { const d = new Date(v); return d.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}); } },
      { label: 'Split into Year + Month', before: ['2024-01-09','2024-03-15','2024-07-22'], after: v => { const d = new Date(v); return `Year: ${d.getFullYear()} · Month: ${d.getMonth()+1}`; } },
      { label: 'Display as DD/MM/YYYY',   before: ['2024-01-09','2024-03-15','2024-07-22'], after: v => { const [y,m,d] = v.split('-'); return `${d}/${m}/${y}`; } },
    ],
    sales:          [
      { label: 'Format as compact ($1.2K)', before: ['528','4655','13707','74','931'], after: v => { const n = Number(v); return n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${n}`; } },
      { label: 'Remove $ prefix (plain number)', before: ['528','4655','13707','74','931'], after: v => Number(v).toLocaleString() },
    ],
    product:        [
      { label: 'Rename column to "Product Name"', before: ['Laptop','Mouse','Keyboard','Monitor','Headphones'], after: v => v },
      { label: 'UPPERCASE all values',            before: ['Laptop','Mouse','Keyboard','Monitor','Headphones'], after: v => v.toUpperCase() },
    ],
    customer_type:  [
      { label: 'Rename column to "Segment"',  before: ['Business','Individual','Education','Government','Healthcare'], after: v => v },
      { label: 'Abbreviate values',            before: ['Business','Individual','Education','Government','Healthcare'], after: v => ({ Business:'Biz', Individual:'Ind.', Education:'Edu', Government:'Gov', Healthcare:'HC' })[v] || v },
    ],
    payment_method: [
      { label: 'Rename to "Payment Type"', before: ['Credit Card','Debit Card','Cash','Bank Transfer','Digital Wallet'], after: v => v },
      { label: 'Abbreviate values',         before: ['Credit Card','Debit Card','Cash','Bank Transfer','Digital Wallet'], after: v => ({ 'Credit Card':'CC','Debit Card':'DC','Cash':'Cash','Bank Transfer':'BT','Digital Wallet':'DW' })[v] || v },
    ],
    quantity:       [
      { label: 'Rename column to "Units Sold"', before: ['6','7','9','1','2'], after: v => v },
      { label: 'Format with "x" suffix (6x)',   before: ['6','7','9','1','2'], after: v => `${v}x` },
    ],
    category:       [
      { label: 'Rename column to "Product Category"', before: ['Accessories','Imaging','Computers','Mobile','Audio'], after: v => v },
      { label: 'UPPERCASE all values',                 before: ['Accessories','Imaging','Computers','Mobile','Audio'], after: v => v.toUpperCase() },
    ],
    // ── Regional Targets ──────────────────────────────────────────────────
    region:         [
      { label: 'UPPERCASE all values',          before: ['North','South','East','West','Central'], after: v => v.toUpperCase() },
      { label: 'Add "Region" suffix',           before: ['North','South','East','West','Central'], after: v => `${v} Region` },
      { label: 'Rename column to "Territory"',  before: ['North','South','East','West','Central'], after: v => v },
    ],
    sales_rep_count:[
      { label: 'Add "reps" suffix',             before: ['12','10','9','11','7'], after: v => `${v} reps` },
      { label: 'Rename column to "Team Size"',  before: ['12','10','9','11','7'], after: v => v },
    ],
    market_size:    [
      { label: 'Convert to numeric score (Large=3, Medium=2, Small=1)', before: ['Large','Medium','Small','Large','Medium'], after: v => ({ Large:'3', Medium:'2', Small:'1' })[v] || v },
      { label: 'UPPERCASE all values',          before: ['Large','Medium','Small','Large','Medium'], after: v => v.toUpperCase() },
      { label: 'Rename column to "Tier"',       before: ['Large','Medium','Small','Large','Medium'], after: v => v },
    ],
    // ── Product Catalog ───────────────────────────────────────────────────
    supplier:       [
      { label: 'UPPERCASE all values',          before: ['TechCorp Ltd','PeriphX Inc','DisplayPro','AudioWave','LensMaster'], after: v => v.toUpperCase() },
      { label: 'Rename column to "Vendor"',     before: ['TechCorp Ltd','PeriphX Inc','DisplayPro','AudioWave','LensMaster'], after: v => v },
    ],
    country_of_origin: [
      { label: 'Rename column to "Made In"',    before: ['Taiwan','China','South Korea','Germany','Japan'], after: v => v },
      { label: 'UPPERCASE all values',          before: ['Taiwan','China','South Korea','Germany','Japan'], after: v => v.toUpperCase() },
    ],
    launch_year:    [
      { label: 'Add "FY" prefix (FY2022)',      before: ['2022','2023','2021','2020','2022'], after: v => `FY${v}` },
      { label: 'Rename column to "Year Released"', before: ['2022','2023','2021','2020','2022'], after: v => v },
    ],
    in_stock:       [
      { label: 'Convert to ✓ / ✗',             before: ['Yes','No','Yes','Yes','No'], after: v => v === 'Yes' ? '✓ In stock' : '✗ Out of stock' },
      { label: 'Rename column to "Available"',  before: ['Yes','No','Yes','Yes','No'], after: v => v },
    ],
  };

  const columns = Object.keys(allData[0]) as (keyof SaleRow)[];

  // ── Filtered data ────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let d = [...allData];
    for (const f of activeFilters) {
      d = d.filter(row => {
        const v = String((row as any)[f.col]).toLowerCase();
        const fv = f.val.toLowerCase();
        if (f.op === 'is') return v === fv;
        if (f.op === 'contains') return v.includes(fv);
        if (f.op === 'is greater than') return parseFloat(v) > parseFloat(fv);
        if (f.op === 'is less than') return parseFloat(v) < parseFloat(fv);
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(q)));
    }
    return d;
  }, [allData, activeFilters, search]);

  const pageData = filteredData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Active dataset rows + columns for Overview — uses helpers (supports joined tabs too)
  const activeRows: any[]   = getTabRows(activeTab);
  const activeCols: string[] = getTabCols(activeTab);
  const activeRowCount = activeRows.length;
  const activeColCount = activeCols.length;
  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

  // ── Proactive insights — recomputed per active tab ───────────────────────
  const insights = useMemo(() => {
    const baseDS = activeTab in DS_META ? DS_META[activeTab as TabDS].baseDS : null;

    // ── Regional Targets dataset ──
    if (baseDS === 'regional' || (!baseDS && activeRows.length > 0 && 'sales_target_2024' in (activeRows[0] || {}))) {
      const rows = activeRows.length > 0 ? activeRows : regionTargets;
      const totalTarget = rows.reduce((s: number, r: any) => s + (Number(r.sales_target_2024) || 0), 0);
      const topTargetRow = [...rows].sort((a: any, b: any) => b.sales_target_2024 - a.sales_target_2024)[0];
      const totalReps = rows.reduce((s: number, r: any) => s + (Number(r.sales_rep_count) || 0), 0);
      const topRepsRow = [...rows].sort((a: any, b: any) => b.sales_rep_count - a.sales_rep_count)[0];
      const largeMarkets = rows.filter((r: any) => r.market_size === 'Large').length;
      return { type: 'regional' as const, totalTarget, topTargetRow, totalReps, topRepsRow, largeMarkets,
        // keep these so callers that reference old fields don't crash
        totalRev: 0, topProduct: ['—', 0] as [string, number], topRegion: ['—', 0] as [string, number],
        avgMargin: 0, topMonth: ['—', 0] as [string, number], discountedCount: 0, top3Rev: 0,
        byProduct: {}, byRegion: {}, byMonth: {} };
    }

    // ── Product Catalog dataset ──
    if (baseDS === 'products' || (!baseDS && activeRows.length > 0 && 'avg_rating' in (activeRows[0] || {}))) {
      const rows = activeRows.length > 0 ? activeRows : productCatalog;
      const avgRating = rows.reduce((s: number, r: any) => s + (Number(r.avg_rating) || 0), 0) / rows.length;
      const topRated = [...rows].sort((a: any, b: any) => b.avg_rating - a.avg_rating)[0];
      const inStockCount = rows.filter((r: any) => r.in_stock === 'Yes').length;
      const newestYear = Math.max(...rows.map((r: any) => Number(r.launch_year) || 0));
      const suppliers = new Set(rows.map((r: any) => r.supplier)).size;
      return { type: 'products' as const, avgRating, topRated, inStockCount, newestYear, suppliers, totalProducts: rows.length,
        totalRev: 0, topProduct: ['—', 0] as [string, number], topRegion: ['—', 0] as [string, number],
        avgMargin: 0, topMonth: ['—', 0] as [string, number], discountedCount: 0, top3Rev: 0,
        byProduct: {}, byRegion: {}, byMonth: {} };
    }

    // ── Sales dataset (default) ──
    const data = activeRows.length > 0 && 'sales' in (activeRows[0] || {}) ? activeRows : allData;
    const totalRev = data.reduce((s: number, r: any) => s + (Number(r.sales) || 0), 0);
    const byProduct: Record<string, number> = {};
    data.forEach((r: any) => { byProduct[r.product] = (byProduct[r.product] || 0) + (Number(r.sales) || 0); });
    const topProduct = Object.entries(byProduct).sort((a,b) => b[1]-a[1])[0] as [string, number];
    const byRegion: Record<string, number> = {};
    data.forEach((r: any) => { byRegion[r.region] = (byRegion[r.region] || 0) + (Number(r.sales) || 0); });
    const topRegion = Object.entries(byRegion).sort((a,b) => b[1]-a[1])[0] as [string, number];
    const avgMargin = data.reduce((s: number, r: any) => s + (Number(r.profit_margin) || 0), 0) / (data.length || 1);
    const byMonth: Record<string, number> = {};
    data.forEach((r: any) => { const m = String(r.date || '').slice(0, 7); byMonth[m] = (byMonth[m] || 0) + (Number(r.sales) || 0); });
    const topMonth = Object.entries(byMonth).sort((a,b) => b[1]-a[1])[0] as [string, number];
    const discountedCount = data.filter((r: any) => Number(r.discount) > 0).length;
    const top3Rev = Object.values(byProduct).sort((a,b)=>b-a).slice(0,3).reduce((s,v)=>s+v,0);
    return { type: 'sales' as const, totalRev, topProduct, topRegion, avgMargin, topMonth, discountedCount, top3Rev, byProduct, byRegion, byMonth };
  }, [activeTab, activeRows, allData, regionTargets, productCatalog]);

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    const rows = getTabRows(chartDataset);
    const cols = getTabCols(chartDataset);
    // Fall back to first valid col if current axis selection doesn't exist in this dataset
    const effX = cols.includes(xAxis) ? xAxis : (cols[0] ?? '');
    const effY = cols.includes(yAxis) ? yAxis : (cols.find(c => c !== effX) ?? cols[0] ?? '');
    const groups: Record<string, number[]> = {};
    for (const row of rows) {
      const key = String((row as any)[effX]);
      if (!groups[key]) groups[key] = [];
      groups[key].push(Number((row as any)[effY]) || 0);
    }
    const agg = (vals: number[]) => {
      if (aggFunc === 'sum') return vals.reduce((a,b)=>a+b,0);
      if (aggFunc === 'count') return vals.length;
      if (aggFunc === 'average') return vals.reduce((a,b)=>a+b,0)/vals.length;
      if (aggFunc === 'max') return Math.max(...vals);
      if (aggFunc === 'min') return Math.min(...vals);
      return 0;
    };
    const entries = Object.entries(groups).map(([k,v]) => [k, agg(v)] as [string, number]);
    entries.sort((a,b) => b[1]-a[1]);
    return { labels: entries.map(e=>e[0]), values: entries.map(e=>e[1]), effX, effY };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDataset, xAxis, yAxis, aggFunc, savedJoins, filteredData]);

  // ── AI responses ──────────────────────────────────────────────────────────
  const getAiResponse = (q: string) => {
    const ql = q.toLowerCase();

    // ── Sales dataset answers ──
    if (insights.type === 'sales') {
      const bp = insights.byProduct as Record<string,number>;
      const br = insights.byRegion as Record<string,number>;
      const bm = insights.byMonth as Record<string,number>;
      const topProds = Object.entries(bp).sort((a,b)=>b[1]-a[1]);
      const topRegs  = Object.entries(br).sort((a,b)=>b[1]-a[1]);
      const topMonths = Object.entries(bm).sort((a,b)=>b[1]-a[1]);

      if (ql.match(/top|best|highest|most|leading|number 1|#1|winner/)) {
        if (ql.match(/product|item|sku|sell/))
          return `🏆 Top products by revenue:\n${topProds.slice(0,5).map((e,i)=>`${i+1}. ${e[0]} — $${(e[1]/1000).toFixed(1)}K`).join('\n')}\n\n${topProds[0][0]} leads with $${(topProds[0][1]/1000).toFixed(1)}K, making up ${((topProds[0][1]/insights.totalRev)*100).toFixed(0)}% of total revenue.`;
        if (ql.match(/region|area|location|territory|geo/))
          return `📍 Top regions by revenue:\n${topRegs.slice(0,5).map((e,i)=>`${i+1}. ${e[0]} — $${(e[1]/1000).toFixed(1)}K`).join('\n')}`;
        if (ql.match(/month|time|period|when/))
          return `📅 Best month: ${topMonths[0][0]} with $${(topMonths[0][1]/1000).toFixed(1)}K in sales.\n\nTop 3 months:\n${topMonths.slice(0,3).map((e,i)=>`${i+1}. ${e[0]} — $${(e[1]/1000).toFixed(1)}K`).join('\n')}`;
      }

      if (ql.match(/worst|lowest|bottom|least|weakest|poor/)) {
        if (ql.match(/product|item/))
          return `📉 Lowest performing products:\n${topProds.slice(-3).reverse().map((e,i)=>`${i+1}. ${e[0]} — $${(e[1]/1000).toFixed(1)}K`).join('\n')}\n\nThese may need a pricing review or marketing push.`;
        if (ql.match(/region|area/))
          return `📉 Lowest revenue regions:\n${topRegs.slice(-3).reverse().map((e,i)=>`${i+1}. ${e[0]} — $${(e[1]/1000).toFixed(1)}K`).join('\n')}`;
      }

      if (ql.match(/revenue|sales|money|earn|generat/))
        return `💰 Total revenue: $${(insights.totalRev/1000).toFixed(0)}K across 1,000 transactions.\n\n• Best product: ${topProds[0][0]} ($${(topProds[0][1]/1000).toFixed(1)}K)\n• Best region: ${topRegs[0][0]} ($${(topRegs[0][1]/1000).toFixed(1)}K)\n• Best month: ${topMonths[0][0]} ($${(topMonths[0][1]/1000).toFixed(1)}K)\n\nTop 3 products account for ${((insights.top3Rev/insights.totalRev)*100).toFixed(0)}% of all revenue — a classic concentration pattern.`;

      if (ql.match(/margin|profit|profitab/))
        return `📊 Average profit margin: ${(insights.avgMargin*100).toFixed(1)}%\n\n• Headphones: ~31% margin (highest)\n• Camera: ~29% margin\n• Printer: ~17% margin (lowest)\n\n💡 Focus discounts on high-margin products like Headphones and Camera — not on Keyboards or Printers where margin is already thin.`;

      if (ql.match(/discount|promo|offer|deal/))
        return `🏷️ ${insights.discountedCount} out of 1,000 orders (${(insights.discountedCount/10).toFixed(0)}%) had a discount applied.\n\nThe problem: discounts are concentrated on low-margin products (Keyboard, Mouse, Printer) where the volume gain doesn't offset the margin loss.\n\n💡 Recommendation: Use discounts selectively on Headphones and Cameras where margins can absorb it and drive meaningful volume.`;

      if (ql.match(/trend|over time|month|seasonal|period|quarterly|q1|q2|q3|q4/))
        return `📅 Sales trend across ${Object.keys(bm).length} months:\n\n• Peak: ${topMonths[0][0]} ($${(topMonths[0][1]/1000).toFixed(1)}K)\n• Slowest: ${topMonths[topMonths.length-1][0]} ($${(topMonths[topMonths.length-1][1]/1000).toFixed(1)}K)\n\nSales peaked in Q3. January–February were the slowest — typical post-holiday dip. Consider promotional pushes in slow months.`;

      if (ql.match(/region|area|location|territory|east|west|north|south|central/))
        return `📍 Revenue breakdown by region:\n${topRegs.map(e=>`• ${e[0]}: $${(e[1]/1000).toFixed(1)}K (${((e[1]/insights.totalRev)*100).toFixed(0)}%)`).join('\n')}`;

      if (ql.match(/product|item|laptop|phone|camera|headphone|keyboard|mouse|printer|tablet|monitor|speaker/))
        return `🛍️ Revenue by product:\n${topProds.map(e=>`• ${e[0]}: $${(e[1]/1000).toFixed(1)}K (${((e[1]/insights.totalRev)*100).toFixed(0)}%)`).join('\n')}`;

      if (ql.match(/how many|count|number of|total order|transaction|row/))
        return `📦 This dataset has 1,000 transactions across ${topProds.length} products and ${topRegs.length} regions.\n\n• ${insights.discountedCount} orders had discounts applied\n• ${1000-insights.discountedCount} orders were full price`;

      if (ql.match(/summar|overview|tell me|what.*data|insight|analys/))
        return `📋 Here's a summary of your sales data:\n\n• Total revenue: $${(insights.totalRev/1000).toFixed(0)}K across 1,000 orders\n• Best product: ${topProds[0][0]} ($${(topProds[0][1]/1000).toFixed(1)}K)\n• Best region: ${topRegs[0][0]}\n• Best month: ${topMonths[0][0]}\n• Avg profit margin: ${(insights.avgMargin*100).toFixed(1)}%\n• ${insights.discountedCount} discounted orders (${(insights.discountedCount/10).toFixed(0)}%)\n\n💡 Key insight: Top 3 products drive ${((insights.top3Rev/insights.totalRev)*100).toFixed(0)}% of all revenue. Margin varies significantly by product — worth reviewing pricing on low-margin items.`;

      if (ql.match(/recommend|suggest|should|improve|action|next step|what to do/))
        return `💡 3 recommendations based on your data:\n\n1. Focus on ${topProds[0][0]} and ${topProds[1][0]} — they drive the most revenue. Make sure they're always in stock.\n\n2. Stop discounting ${topProds[topProds.length-1][0]} and ${topProds[topProds.length-2][0]} — lowest revenue AND thin margins mean discounts hurt more than they help.\n\n3. Invest in ${topRegs[topRegs.length-1][0]} region — it's your lowest performing area and likely has untapped demand.`;

      // Generic fallback for sales
      return `I looked through your sales data to answer "${q}".\n\nHere's what I found:\n• Total revenue: $${(insights.totalRev/1000).toFixed(0)}K\n• Top product: ${topProds[0][0]} ($${(topProds[0][1]/1000).toFixed(1)}K)\n• Top region: ${topRegs[0][0]}\n• Avg margin: ${(insights.avgMargin*100).toFixed(1)}%\n\nTry asking about a specific product, region, month, margin, or discounts for a more targeted answer.`;
    }

    // ── Regional Targets answers ──
    if (insights.type === 'regional') {
      if (ql.match(/target|goal|quota/))
        return `🎯 Total 2024 sales target: $${(insights.totalTarget/1000).toFixed(0)}K across all regions.\n\nHighest target: ${(insights.topTargetRow as any)?.region} at $${(Number((insights.topTargetRow as any)?.sales_target_2024)/1000).toFixed(0)}K`;
      if (ql.match(/rep|team|staff|headcount|people/))
        return `👥 Total sales reps across all regions: ${insights.totalReps}\n\nRegion with most reps: ${(insights.topRepsRow as any)?.region} (${(insights.topRepsRow as any)?.sales_rep_count} reps)`;
      if (ql.match(/large|market|size/))
        return `🏙️ ${insights.largeMarkets} out of the regions are classified as Large markets.\n\nLarge markets typically receive higher targets and more sales rep allocation.`;
      return `📋 Regional Targets summary:\n• Total 2024 target: $${(insights.totalTarget/1000).toFixed(0)}K\n• Total reps: ${insights.totalReps}\n• Large markets: ${insights.largeMarkets}\n• Highest target region: ${(insights.topTargetRow as any)?.region}`;
    }

    // ── Product Catalog answers ──
    if (insights.type === 'products') {
      if (ql.match(/rating|review|score|star/))
        return `⭐ Average product rating: ${insights.avgRating.toFixed(2)} / 5\n\nTop rated: ${(insights.topRated as any)?.product_name} (${(insights.topRated as any)?.avg_rating} stars)`;
      if (ql.match(/stock|available|inventory/))
        return `📦 ${insights.inStockCount} out of ${insights.totalProducts} products are currently in stock.\n\n${insights.totalProducts - insights.inStockCount} products are out of stock and may need restocking.`;
      if (ql.match(/supplier|vendor|partner/))
        return `🏭 Products come from ${insights.suppliers} different suppliers.\n\nDiversifying suppliers reduces risk — ${insights.suppliers > 3 ? 'your supplier mix looks healthy.' : 'consider adding more suppliers for resilience.'}`;
      return `📋 Product Catalog summary:\n• ${insights.totalProducts} total products\n• Avg rating: ${insights.avgRating.toFixed(2)} / 5\n• In stock: ${insights.inStockCount}\n• Suppliers: ${insights.suppliers}\n• Newest launch year: ${insights.newestYear}`;
    }

    return `I'm not sure how to answer that specific question in demo mode.\n\nTry asking about: products, regions, revenue, margins, discounts, trends, or recommendations.\n\n💡 Sign up free to connect your own data and get real AI answers powered by GPT-4.`;
  };

  const sendAi = () => {
    if (!aiInput.trim()) return;
    const q = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', text: q }, { role: 'ai', text: getAiResponse(q) }]);
  };

  // ─── CSV export ──────────────────────────────────────────────────────────
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  const exportCSV = (ds: 'sales'|'regional'|'products', cleaned: boolean) => {
    setExportAnchor(null);

    const rows: any[] = ds === 'regional' ? regionTargets : ds === 'products' ? productCatalog : filteredData;
    const cols: string[] = ds === 'regional' ? Object.keys(regionTargets[0])
      : ds === 'products' ? Object.keys(productCatalog[0])
      : columns as string[];

    const snap: Set<string> = cleaned ? (savedAcceptedMap[ds] ?? new Set()) : new Set();

    const applyClean = (col: string, val: any): string => {
      if (cleaned && snap.has(col)) {
        if (col === 'profit_margin')     return `${Math.round(parseFloat(String(val)) * 100)}%`;
        if (col === 'discount')          return `${val}%`;
        if (col === 'region')            return String(val).trim();
        if (col === 'sales_target_2024') return `$${(Number(val) / 1000).toFixed(0)}K`;
        if (col === 'territory_manager') { const [f, ...r] = String(val).split(' '); return `First: ${f} · Last: ${r.join(' ')}`; }
        if (col === 'avg_rating')        return `${val} / 5`;
        if (col === 'warranty_years')    return `${val} yr`;
      }
      return val == null ? '' : String(val);
    };

    const header = cols.join(',');
    const body = rows.map(row =>
      cols.map(col => {
        const cell = applyClean(col, (row as any)[col]);
        return /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
      }).join(',')
    ).join('\n');

    const fileNames: Record<string, string> = {
      sales: 'sales_2024', regional: 'regional_targets', products: 'product_catalog',
    };
    const filename = `${fileNames[ds]}${cleaned ? '_cleaned' : ''}.csv`;

    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Which dataset is "in context" for the export menu (joined tabs have no CSV export)
  const exportContextDS: DS | null =
    tab === 'clean' ? cleanDataset
    : isTabJoined(activeTab) ? null
    : getTabBaseDS(activeTab);

  const exportDSLabel: Record<'sales'|'regional'|'products', string> = {
    sales: 'sales_2024.csv', regional: 'Regional Targets', products: 'Product Catalog',
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#F7F8FA', overflow: 'hidden', width: '100%', maxWidth: '100vw' }}>

      {/* Top bar */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E5E7EB', px: { xs: 1.5, md: 3 }, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Button startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />} onClick={onBack}
          sx={{ color: '#374151', fontWeight: 500, fontSize: '0.875rem', textTransform: 'none' }}>Back</Button>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111', letterSpacing: '-0.02em' }}>DataScope</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Export / Download">
            <IconButton size="small" onClick={e => setExportAnchor(e.currentTarget)}>
              <FileDownloadIcon sx={{ fontSize: 17, color: exportAnchor ? '#111' : '#6B7280' }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 0.5, borderRadius: '10px', border: '1px solid #E5E7EB', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', minWidth: 240 } }}>
            {/* Header */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Export dataset
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontWeight: 600, mt: 0.25 }}>
                {exportContextDS ? exportDSLabel[exportContextDS] : getTabLabel(activeTab)}
              </Typography>
            </Box>
            <Divider />
            {exportContextDS ? (
              <>
                {/* Original */}
                <MenuItem onClick={() => exportCSV(exportContextDS, false)}
                  sx={{ py: 1.25, px: 2, gap: 1.5, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <FileDownloadIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>Original — .csv</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>Unmodified raw data</Typography>
                  </Box>
                </MenuItem>
                {/* Cleaned — only if saved */}
                {cleanSavedMap[exportContextDS] ? (
                  <MenuItem onClick={() => exportCSV(exportContextDS, true)}
                    sx={{ py: 1.25, px: 2, gap: 1.5, '&:hover': { bgcolor: '#F0FDF4' } }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckIcon sx={{ fontSize: 10, color: '#16A34A' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#16A34A' }}>Cleaned — .csv</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                        {(savedAcceptedMap[exportContextDS]?.size ?? 0)} fix{(savedAcceptedMap[exportContextDS]?.size ?? 0) !== 1 ? 'es' : ''} applied
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : (
                  <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, opacity: 0.45 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckIcon sx={{ fontSize: 10, color: '#9CA3AF' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#6B7280' }}>Cleaned — .csv</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>Run Clean tab to unlock</Typography>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>Joined tables can't be exported in demo mode.</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.5 }}>
                  <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span> to export.
                </Typography>
              </Box>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Dataset info bar */}
      <Box sx={{ bgcolor: '#EFF6FF', borderBottom: '1px solid #DBEAFE', px: { xs: 1.5, md: 3 }, py: 1, display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2.5 }, flexShrink: 0, flexWrap: 'wrap' }}>

        {/* Datasets multi-select — master list, checked = tab shown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon sx={{ fontSize: 14, color: '#3B82F6' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#3B82F6' }}>Datasets</Typography>
          <Select
            multiple
            size="small"
            value={tabDatasets}
            onChange={e => {
              const val = e.target.value as string[];
              if (val.length === 0) return;
              setTabDatasets(val);
              if (!val.includes(activeTab)) setActiveTab(val[0]);
              const baseDSsInVal = val.filter(d => d in DS_META).map(d => DS_META[d as TabDS].baseDS);
              if (baseDSsInVal.length > 0 && !baseDSsInVal.includes(cleanDataset)) setCleanDataset(baseDSsInVal[0]);
              if (!val.includes(joinLeft))  setJoinLeft((val.find(d => d in DS_META) ?? val[0]) as TabDS);
              if (!val.includes(joinRight)) setJoinRight((val.find(d => d in DS_META && d !== joinLeft) ?? val[0]) as TabDS);
            }}
            renderValue={() => `${tabDatasets.length} dataset${tabDatasets.length !== 1 ? 's' : ''}`}
            sx={{
              fontSize: '0.75rem', fontWeight: 600, color: '#1D4ED8',
              bgcolor: '#fff', border: '1px solid #BFDBFE', borderRadius: '6px',
              height: 26,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { py: 0, px: 1.25 },
            }}>
            {allAvailableDatasets.map(ds => (
              <MenuItem key={ds} value={ds} sx={{ py: 0.5 }}>
                <Checkbox size="small" checked={tabDatasets.includes(ds)} sx={{ p: 0.5 }} />
                <ListItemText
                  primary={getTabLabel(ds)}
                  primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
                />
                {isTabCleaned(ds) && (
                  <Box sx={{ ml: 1, width: 14, height: 14, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckIcon sx={{ fontSize: 9, color: '#16A34A' }} />
                  </Box>
                )}
                {isTabJoined(ds) && (
                  <Box sx={{ ml: 1, width: 14, height: 14, borderRadius: '50%', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MergeTypeIcon sx={{ fontSize: 9, color: '#3B82F6' }} />
                  </Box>
                )}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Typography sx={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: 500 }}>{activeRowCount.toLocaleString()} rows</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: 500 }}>{activeColCount} cols</Typography>
        {activeFilters.length > 0 && (
          <Chip label={`${activeFilters.length} filter${activeFilters.length > 1 ? 's' : ''} active`} size="small"
            onDelete={() => setActiveFilters([])}
            sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
        )}
        <Box sx={{ ml: { xs: 0, md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
          <Chip label="DEMO" size="small" sx={{ bgcolor: '#EF4444', color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
          <Button size="small" onClick={onSignUp} sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1D4ED8', textTransform: 'none' }}>
            Sign up to use your own data →
          </Button>
        </Box>
      </Box>

      {/* Tabs — horizontally scrollable on mobile */}
      <Box sx={{
        bgcolor: '#fff', borderBottom: '1px solid #E5E7EB',
        flexShrink: 0, minWidth: 0,
        overflowX: 'auto', overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none' as any, scrollbarWidth: 'none' as any,
      }}>
        <Box sx={{ display: 'inline-flex', gap: 0, minWidth: 'max-content', px: { xs: 0.5, sm: 1, md: 3 } }}>
          {TABS.map(t => (
            <Button key={t.id} startIcon={t.icon}
              onClick={() => setTab(t.id)}
              sx={{
                textTransform: 'none', fontWeight: tab === t.id ? 700 : 500,
                fontSize: '0.82rem',
                px: 1.5, py: 1, borderRadius: 0, mb: '-1px',
                minWidth: 'auto', whiteSpace: 'nowrap',
                color: tab === t.id ? '#111' : '#6B7280',
                bgcolor: 'transparent',
                borderBottom: tab === t.id ? '2px solid #111' : '2px solid transparent',
                '&:hover': { bgcolor: '#F9FAFB', color: '#111' },
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}>{t.label}</Button>
          ))}
        </Box>
      </Box>

      {/* ── Tab content ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* ══ EXPLORE ══ */}
        {tab === 'explore' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

            {/* Excel-style dataset tab strip */}
            <Box sx={{ flexShrink: 0, position: 'relative', bgcolor: '#E5E7EB', borderBottom: '1px solid #9CA3AF' }}>
              {/* Fade gradient — hints at more tabs off-screen */}
              {tabDatasets.length > 3 && (
                <Box sx={{
                  position: 'absolute', right: 0, top: 0, bottom: 0, width: 40, zIndex: 2,
                  background: 'linear-gradient(to right, transparent, #E5E7EB)',
                  pointerEvents: 'none',
                }} />
              )}
            <Box sx={{
              overflowX: 'auto', overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin' as any,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: '#E5E7EB' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#9CA3AF', borderRadius: 2 },
              display: 'flex', alignItems: 'flex-end',
              px: 1.5, pt: 0.75,
            }}>
              {tabDatasets.map(ds => {
                const isActive = activeTab === ds;
                return (
                  <Box key={ds}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      pl: 1.5, pr: 0.75, py: 0.6, mr: '2px',
                      borderRadius: '5px 5px 0 0', border: '1px solid',
                      borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                      borderBottom: isActive ? '1px solid #F7F8FA' : '1px solid #C9CDD4',
                      bgcolor: isActive ? '#F7F8FA' : '#D1D5DB',
                      mb: '-1px', cursor: isActive ? 'default' : 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                    }}>
                    <Typography
                      onClick={() => setActiveTab(ds)}
                      sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563', whiteSpace: 'nowrap', userSelect: 'none' }}>
                      {getTabLabel(ds)}
                    </Typography>
                    {isTabCleaned(ds) && (
                      <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                        <CheckIcon sx={{ fontSize: 8, color: '#16A34A' }} />
                      </Box>
                    )}
                    {isTabJoined(ds) && (
                      <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                        <MergeTypeIcon sx={{ fontSize: 8, color: '#3B82F6' }} />
                      </Box>
                    )}
                    {/* Close (×) — removes from visible tabs, keeps in dropdown */}
                    <Box
                      onClick={e => {
                        e.stopPropagation();
                        const next = tabDatasets.filter(d => d !== ds);
                        if (next.length === 0) return;
                        setTabDatasets(next);
                        if (activeTab === ds) setActiveTab(next[0]);
                      }}
                      sx={{
                        ml: 0.5, width: 14, height: 14, borderRadius: '3px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#9CA3AF', cursor: 'pointer', flexShrink: 0,
                        '&:hover': { bgcolor: '#E5E7EB', color: '#374151' },
                      }}>
                      <CloseIcon sx={{ fontSize: 10 }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
            </Box>

            {/* Cleaned dataset banner */}
            {viewingCleaned && (
              <Box sx={{ mx: { xs: 1.5, md: 3 }, mt: 2, mb: 0, p: 1.5, bgcolor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#16A34A' }}>
                    Viewing: {getTabLabel(activeTab)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                    · {activeCleanedAccepted.size} change{activeCleanedAccepted.size !== 1 ? 's' : ''} applied
                  </Typography>
                </Box>
                <Button size="small"
                  onClick={() => setActiveTab(getTabBaseDS(activeTab) ?? activeTab)}
                  sx={{ fontSize: '0.72rem', color: '#6B7280', textTransform: 'none', border: '1px solid #D1FAE5', borderRadius: '6px', px: 1.5 }}>
                  Switch to original
                </Button>
              </Box>
            )}

            {/* Proactive insights row */}
            <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2, pb: 1.5, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 15, color: '#F59E0B' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  Auto Insights
                </Typography>
              </Box>
              {/* Mobile: horizontally scrollable row. Desktop: grid */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                overflowX: { xs: 'auto', md: 'visible' },
                flexWrap: { xs: 'nowrap', md: 'wrap' },
                pb: { xs: 1, md: 0 },
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: 2 },
              }}>
                {insights.type === 'regional' ? (<>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#3B82F6"
                      title="Total Target" value={`$${(insights.totalTarget!/1000).toFixed(0)}K`} sub="Combined 2024 sales target" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<BarChartIcon sx={{ fontSize: 18 }} />} color="#10B981"
                      title="Highest Target" value={insights.topTargetRow!.region} sub={`$${(insights.topTargetRow!.sales_target_2024/1000).toFixed(0)}K target`} />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />} color="#8B5CF6"
                      title="Total Sales Reps" value={String(insights.totalReps!)} sub="Across all regions" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="#F59E0B"
                      title="Most Reps" value={insights.topRepsRow!.region} sub={`${insights.topRepsRow!.sales_rep_count} reps assigned`} />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#EF4444"
                      title="Large Markets" value={String(insights.largeMarkets!)} sub={`of ${regionTargets.length} regions`} />
                  </Box>
                </>) : insights.type === 'products' ? (<>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#3B82F6"
                      title="Products" value={String(insights.totalProducts!)} sub="In catalog" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<BarChartIcon sx={{ fontSize: 18 }} />} color="#10B981"
                      title="Avg Rating" value={insights.avgRating!.toFixed(1)} sub="Across all products" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />} color="#8B5CF6"
                      title="Top Rated" value={insights.topRated!.product} sub={`${insights.topRated!.avg_rating} ★ rating`} />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="#F59E0B"
                      title="In Stock" value={`${insights.inStockCount!}/${insights.totalProducts!}`} sub="Available now" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#EF4444"
                      title="Suppliers" value={String(insights.suppliers!)} sub="Unique vendors" />
                  </Box>
                </>) : (<>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#3B82F6"
                      title="Total Revenue" value={`$${(insights.totalRev/1000).toFixed(0)}K`} sub="Across 1,000 transactions" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<BarChartIcon sx={{ fontSize: 18 }} />} color="#10B981"
                      title="Top Product" value={insights.topProduct[0]} sub={`$${(insights.topProduct[1]/1000).toFixed(1)}K revenue`} />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />} color="#8B5CF6"
                      title="Best Region" value={insights.topRegion[0]} sub={`$${(insights.topRegion[1]/1000).toFixed(1)}K`} />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="#F59E0B"
                      title="Avg Margin" value={`${(insights.avgMargin*100).toFixed(1)}%`} sub="Blended profit margin" />
                  </Box>
                  <Box sx={{ minWidth: { xs: 180, md: 'auto' }, flex: { md: 1 } }}>
                    <InsightCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#EF4444"
                      title="Top 3 Products" value={`${insights.totalRev > 0 ? ((insights.top3Rev/insights.totalRev)*100).toFixed(0) : 0}%`} sub="of total revenue (80/20 rule)" />
                  </Box>
                </>)}
              </Box>
            </Box>

            {/* Search */}
            <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search across all columns…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#9CA3AF' }} /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 300 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.825rem', bgcolor: '#fff' } }}
              />
              <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>{activeRowCount.toLocaleString()} rows</Typography>
            </Box>

            {/* Table */}
            <Box sx={{ flex: 1, overflow: 'auto', mx: { xs: 1, md: 3 }, mb: 0, border: '1px solid #E5E7EB', borderRadius: '10px', bgcolor: '#fff' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${activeCols.length}, minmax(130px,1fr))`, borderBottom: '2px solid #E5E7EB', position: 'sticky', top: 0, bgcolor: '#F9FAFB', zIndex: 1 }}>
                {activeCols.map(col => (
                  <Box key={col} sx={{ px: 2, py: 1.25 }}>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', whiteSpace: 'nowrap' }}>
                      {col.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {(getTabBaseDS(activeTab) === 'sales' && !isTabJoined(activeTab) ? pageData : activeRows).map((row, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${activeCols.length}, minmax(130px,1fr))`, borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#F9FAFB' } }}>
                  {activeCols.map(col => (
                    <Box key={col} sx={{ px: 2, py: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(() => {
                          if (viewingCleaned && activeCleanedAccepted.has(col)) {
                            if (col === 'profit_margin') return `${Math.round(parseFloat(String(row[col])) * 100)}%`;
                            if (col === 'discount') return `${row[col]}%`;
                            if (col === 'region') return String(row[col]).trim();
                            if (col === 'sales_target_2024') return `$${(Number(row[col]) / 1000).toFixed(0)}K`;
                            if (col === 'territory_manager') { const [f, ...r2] = String(row[col]).split(' '); return `First: ${f} · Last: ${r2.join(' ')}`; }
                            if (col === 'avg_rating') return `${row[col]} / 5`;
                            if (col === 'warranty_years') return `${row[col]} yr`;
                          }
                          return formatCell(row[col], detectColumnType(col, activeRows.map((r: any) => r[col])));
                        })()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            <Box sx={{ px: { xs: 1.5, md: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexShrink: 0, flexWrap: 'wrap' }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage(p => p-1)} sx={{ fontSize: '0.75rem', minWidth: 'auto' }}>← Prev</Button>
              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>Page {page+1}/{totalPages}</Typography>
              <Button size="small" disabled={page >= totalPages-1} onClick={() => setPage(p => p+1)} sx={{ fontSize: '0.75rem', minWidth: 'auto' }}>Next →</Button>
            </Box>
          </Box>
        )}

        {/* ══ CLEAN ══ */}
        {tab === 'clean' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* ── Excel-style sheet tab bar (top) ── */}
            <Box sx={{
              flexShrink: 0,
              bgcolor: '#E5E7EB',
              borderBottom: '1px solid #9CA3AF',
              overflowX: 'auto', overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none' as any, scrollbarWidth: 'none' as any,
              display: 'flex', alignItems: 'flex-end',
              px: 1.5, pt: 1,
            }}>
              {(['sales','regional','products'] as DS[]).filter(k => tabDatasets.some(t => (t in DS_META) && DS_META[t as TabDS].baseDS === k)).map(key => ({ key, label: DS_META[key].label })).map(ds => {
                const isActive = cleanDataset === ds.key;
                const isSaved  = !!cleanSavedMap[ds.key];
                return (
                  <Box
                    key={ds.key}
                    onClick={() => {
                      if (isActive) return;
                      setCleanDataset(ds.key);
                      setCleanScanned(false);
                      setCleanAccepted(new Set());
                      setCleanSkipped(new Set());
                      setOverrideExpanded(null);
                      setOverrideAccepted(new Set());
                      setOverrideAction({});
                    }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75,
                      px: 2, py: 0.75,
                      mr: '2px',
                      cursor: isActive ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      borderRadius: '5px 5px 0 0',
                      border: '1px solid',
                      borderBottom: isActive ? '1px solid #fff' : '1px solid #C9CDD4',
                      borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                      bgcolor: isActive ? '#fff' : '#D1D5DB',
                      mb: '-1px',
                      transition: 'background-color 0.15s',
                      '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                    }}>
                    <Typography sx={{
                      fontSize: '0.78rem',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? '#111' : '#4B5563',
                    }}>
                      {ds.label}
                    </Typography>
                    {isSaved && (
                      <Box sx={{
                        width: 15, height: 15, borderRadius: '50%',
                        bgcolor: isActive ? '#DCFCE7' : '#C8E6C9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <CheckIcon sx={{ fontSize: 9, color: '#16A34A' }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Scrollable content area */}
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111' }}>AI Data Health Check</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mt: 0.25 }}>
                  AI reads every column in <strong>{cleanDatasetLabel}</strong> and recommends ways to make your data easier to read and analyse.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {!cleanScanned && (
                  <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                    onClick={() => setCleanScanned(true)}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#333' } }}>
                    Run AI Scan
                  </Button>
                )}
                {cleanScanned && (
                  <Chip label="Scanned ✓" size="small"
                    sx={{ bgcolor: '#F0FDF4', color: '#16A34A', fontWeight: 700, fontSize: '0.72rem', border: '1px solid #86EFAC' }} />
                )}
              </Box>
            </Box>

            {/* Pre-scan prompt */}
            {!cleanScanned && (
              <Box sx={{ border: '2px dashed #E5E7EB', borderRadius: '12px', p: 6, textAlign: 'center', bgcolor: '#FAFAFA' }}>
                <AutoAwesomeIcon sx={{ fontSize: 44, color: '#D1D5DB', mb: 2 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#374151', mb: 1 }}>AI hasn't scanned this dataset yet</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', mb: 3 }}>
                  Click "Run AI Scan" above — AI will read every column and suggest ways to make your data clearer before you filter, join or chart it.
                </Typography>
              </Box>
            )}

            {cleanScanned && (
              <>
                {/* Summary bar — recommendations LAST */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 3 }}>
                  {[
                    { label: 'Columns scanned', value: '10',  color: '#3B82F6', bg: '#EFF6FF' },
                    { label: 'Duplicate rows',  value: '0',   color: '#10B981', bg: '#F0FDF4' },
                    { label: 'Blank values',    value: '0',   color: '#10B981', bg: '#F0FDF4' },
                    { label: 'AI Recommendations', value: String(cleanIssueCount), color: '#7C3AED', bg: '#F5F3FF' },
                  ].map(s => (
                    <Box key={s.label} sx={{ p: 2, bgcolor: s.bg, borderRadius: '10px', border: `1px solid ${s.color}22` }}>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.5 }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Recommendation cards */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1.5 }}>
                  ✦ AI Recommendations ({cleanIssueCount})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  {activeCleanRecs.filter(r => r.status === 'warning').map(rec => {
                    const accepted = cleanAccepted.has(rec.col);
                    const skipped  = cleanSkipped.has(rec.col);

                    // Compute before/after values for live preview
                    const afterValues: string[] = rec.sampleValues.map(v => {
                      if (rec.col === 'profit_margin')     return `${Math.round(parseFloat(v) * 100)}%`;
                      if (rec.col === 'discount')          return `${v}%`;
                      if (rec.col === 'region')            return v.trim();
                      if (rec.col === 'sales_target_2024') return `$${(Number(v) / 1000).toFixed(0)}K`;
                      if (rec.col === 'territory_manager') {
                        const [first, ...rest] = v.split(' ');
                        return `First: ${first} · Last: ${rest.join(' ')}`;
                      }
                      if (rec.col === 'avg_rating')        return `${v} / 5`;
                      if (rec.col === 'warranty_years')    return `${v} yr`;
                      return v;
                    });

                    return (
                      <Box key={rec.col} sx={{
                        border: `1px solid ${accepted ? '#86EFAC' : skipped ? '#E5E7EB' : '#E9D5FF'}`,
                        borderRadius: '12px', bgcolor: accepted ? '#F0FDF4' : skipped ? '#FAFAFA' : '#FDFBFF',
                        overflow: 'hidden', opacity: skipped ? 0.55 : 1,
                        transition: 'all 0.2s ease',
                      }}>
                        {/* Card header */}
                        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                            {accepted
                              ? <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                              : <AutoAwesomeIcon sx={{ fontSize: 18, color: '#7C3AED' }} />}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', fontFamily: 'monospace' }}>{rec.col}</Typography>
                              <Chip label={rec.action} size="small"
                                sx={{ bgcolor: accepted ? '#DCFCE7' : '#EDE9FE', color: accepted ? '#16A34A' : '#7C3AED', fontSize: '0.68rem', height: 20, fontWeight: 700 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.65 }}>
                              <strong style={{ color: '#7C3AED' }}>AI recommends:</strong> {rec.recommendation}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Live before / after preview table */}
                        <Box sx={{ mx: 2.5, mb: 2, border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                            <Box sx={{ px: 2, py: 1, borderRight: '1px solid #E5E7EB' }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current</Typography>
                            </Box>
                            <Box sx={{ px: 2, py: 1, bgcolor: accepted ? '#F0FDF4' : '#FAFAFA' }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: accepted ? '#16A34A' : '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                After fix {accepted ? '✓' : '(preview)'}
                              </Typography>
                            </Box>
                          </Box>
                          {rec.sampleValues.map((before, i) => (
                            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: i < rec.sampleValues.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <Box sx={{ px: 2, py: 0.9, borderRight: '1px solid #F3F4F6' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#EF4444' }}>{before}</Typography>
                              </Box>
                              <Box sx={{ px: 2, py: 0.9, bgcolor: accepted ? '#F0FDF4' : '#FAFEFF' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: accepted ? '#16A34A' : '#6B7280', fontWeight: accepted ? 600 : 400 }}>
                                  {afterValues[i]}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!accepted && !skipped && (
                            <>
                              <Button size="small" startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setCleanAccepted(s => { const n = new Set(s); n.add(rec.col); return n; })}
                                sx={{ bgcolor: '#7C3AED', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', px: 2,
                                  '&:hover': { bgcolor: '#6D28D9' } }}>
                                Accept — {rec.action}
                              </Button>
                              <Button size="small" startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setCleanSkipped(s => { const n = new Set(s); n.add(rec.col); return n; })}
                                sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', border: '1px solid #E5E7EB', px: 2 }}>
                                Skip for now
                              </Button>
                            </>
                          )}
                          {accepted && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={`✓ ${rec.action} applied`} size="small"
                                sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: '0.72rem', height: 22 }} />
                              <Button size="small"
                                onClick={() => setCleanAccepted(s => { const n = new Set(s); n.delete(rec.col); return n; })}
                                sx={{ fontSize: '0.72rem', color: '#9CA3AF', textTransform: 'none' }}>Undo</Button>
                            </Box>
                          )}
                          {skipped && (
                            <Button size="small"
                              onClick={() => setCleanSkipped(s => { const n = new Set(s); n.delete(rec.col); return n; })}
                              sx={{ fontSize: '0.75rem', color: '#7C3AED', textTransform: 'none', fontWeight: 600 }}>
                              ↩ Reconsider
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Looks good columns */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1.5 }}>
                  ✓ Looks good ({activeCleanRecs.filter(r => r.status === 'good').length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                  {activeCleanRecs.filter(r => r.status === 'good').map(rec => {
                    const isExpanded = overrideExpanded === rec.col;
                    const accepted   = overrideAccepted.has(rec.col);
                    const options    = OVERRIDE_OPTIONS[rec.col] || [];
                    const chosen     = overrideAction[rec.col];
                    const chosenOpt  = options.find(o => o.label === chosen);

                    return (
                      <Box key={rec.col} sx={{
                        border: `1px solid ${accepted ? '#86EFAC' : isExpanded ? '#BFDBFE' : '#D1FAE5'}`,
                        borderRadius: '10px', bgcolor: accepted ? '#F0FDF4' : isExpanded ? '#EFF6FF' : '#F0FDF4',
                        overflow: 'hidden', transition: 'all 0.15s ease',
                      }}>
                        {/* Collapsed row */}
                        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: accepted ? '#16A34A' : '#16A34A', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#111', fontFamily: 'monospace' }}>{rec.col}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', lineHeight: 1.5 }} noWrap>{rec.recommendation}</Typography>
                          </Box>
                          {accepted && (
                            <Chip label={`✓ ${chosen}`} size="small"
                              sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: '0.65rem', height: 20, mr: 1 }} />
                          )}
                          {options.length > 0 && (
                            <Button size="small"
                              onClick={() => setOverrideExpanded(isExpanded ? null : rec.col)}
                              sx={{ fontSize: '0.72rem', color: isExpanded ? '#1D4ED8' : '#6B7280', textTransform: 'none', fontWeight: 600,
                                whiteSpace: 'nowrap', flexShrink: 0, px: 1.5, borderRadius: '6px',
                                border: `1px solid ${isExpanded ? '#BFDBFE' : '#E5E7EB'}`,
                                bgcolor: isExpanded ? '#EFF6FF' : 'transparent' }}>
                              {isExpanded ? 'Close ×' : 'Customise ›'}
                            </Button>
                          )}
                        </Box>

                        {/* Expanded override panel */}
                        {isExpanded && (
                          <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #DBEAFE' }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#1D4ED8', fontWeight: 600, mt: 1.5, mb: 1 }}>
                              Choose an action to apply to <span style={{ fontFamily: 'monospace' }}>{rec.col}</span>:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                              {options.map(opt => (
                                <Box key={opt.label}
                                  onClick={() => setOverrideAction(a => ({ ...a, [rec.col]: opt.label }))}
                                  sx={{ px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer',
                                    border: `1px solid ${chosen === opt.label ? '#3B82F6' : '#E5E7EB'}`,
                                    bgcolor: chosen === opt.label ? '#EFF6FF' : '#fff',
                                    '&:hover': { borderColor: '#93C5FD', bgcolor: '#F0F9FF' } }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: chosen === opt.label ? 700 : 500, color: chosen === opt.label ? '#1D4ED8' : '#374151' }}>
                                    {chosen === opt.label ? '● ' : '○ '}{opt.label}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* Live before/after preview for chosen option */}
                            {chosenOpt && (
                              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', mb: 1.5 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                  <Box sx={{ px: 2, py: 1, borderRight: '1px solid #E5E7EB' }}>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current</Typography>
                                  </Box>
                                  <Box sx={{ px: 2, py: 1 }}>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>After (preview)</Typography>
                                  </Box>
                                </Box>
                                {chosenOpt.before.map((v, i) => (
                                  <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: i < chosenOpt.before.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                                    <Box sx={{ px: 2, py: 0.9, borderRight: '1px solid #F3F4F6' }}>
                                      <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#6B7280' }}>{v}</Typography>
                                    </Box>
                                    <Box sx={{ px: 2, py: 0.9, bgcolor: '#F0F9FF' }}>
                                      <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#1D4ED8', fontWeight: 600 }}>{chosenOpt.after(v)}</Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" disabled={!chosen}
                                onClick={() => { setOverrideAccepted(s => { const n = new Set(s); n.add(rec.col); return n; }); setOverrideExpanded(null); }}
                                sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', px: 2,
                                  '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                                Apply
                              </Button>
                              <Button size="small" onClick={() => { setOverrideExpanded(null); setOverrideAction(a => ({ ...a, [rec.col]: '' })); }}
                                sx={{ color: '#6B7280', textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', border: '1px solid #E5E7EB', px: 2 }}>
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Save cleaned */}
                <Box sx={{ border: `1px solid ${cleanSaved ? '#86EFAC' : '#E5E7EB'}`, borderRadius: '12px', p: 3, bgcolor: cleanSaved ? '#F0FDF4' : '#fff', transition: 'all 0.2s' }}>
                  {!cleanSaved ? (
                    <>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', mb: 0.5 }}>Save as a cleaned copy</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                        Your original <strong>{cleanDatasetLabel}</strong> stays untouched. A new copy called{' '}
                        <strong>"{cleanDatasetLabel} — Cleaned"</strong> will be saved with {cleanAcceptedCount} recommendation{cleanAcceptedCount !== 1 ? 's' : ''} applied.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" startIcon={<CleaningServicesIcon />}
                          disabled={cleanAcceptedCount === 0}
                          onClick={() => {
                            const cleanedTabId = `${cleanDataset}_cleaned` as TabDS;
                            setCleanSavedMap(m => ({ ...m, [cleanDataset]: true }));
                            setSavedAcceptedMap(m => ({ ...m, [cleanDataset]: new Set(cleanAccepted) }));
                            // Auto-add the cleaned tab if not already visible
                            setTabDatasets(prev => prev.includes(cleanedTabId) ? prev : [...prev, cleanedTabId]);
                          }}
                          sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
                            '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                          Save "{cleanDatasetLabel} — Cleaned"
                        </Button>
                        {cleanAcceptedCount === 0 && (
                          <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF' }}>Accept at least one recommendation to save</Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const snap = savedAcceptedMap[cleanDataset] ?? new Set<string>();
                        const snapCount = snap.size;
                        return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#16A34A' }} />
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#16A34A' }}>Saved — {cleanDatasetLabel} — Cleaned</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{snapCount} recommendation{snapCount !== 1 ? 's' : ''} applied · available for this session</Typography>
                        </Box>
                      </Box>
                        );
                      })()}

                      {/* What was applied summary */}
                      <Box sx={{ bgcolor: '#fff', border: '1px solid #D1FAE5', borderRadius: '8px', p: 2, mb: 2 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Changes applied</Typography>
                        {activeCleanRecs.filter(r => r.status === 'warning' && (savedAcceptedMap[cleanDataset] ?? new Set()).has(r.col)).map(r => (
                          <Box key={r.col} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CheckIcon sx={{ fontSize: 13, color: '#16A34A' }} />
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                              <strong style={{ fontFamily: 'monospace' }}>{r.col}</strong> — {r.action}
                            </Typography>
                          </Box>
                        ))}
                        {Object.entries(overrideAction).filter(([col]) => overrideAccepted.has(col)).map(([col, action]) => (
                          <Box key={col} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CheckIcon sx={{ fontSize: 13, color: '#16A34A' }} />
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                              <strong style={{ fontFamily: 'monospace' }}>{col}</strong> — {action}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined"
                          onClick={() => {
                            const cleanedTabId = `${cleanDataset}_cleaned` as TabDS;
                            setActiveTab(cleanedTabId);
                            setTab('explore');
                          }}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', borderColor: '#16A34A', color: '#16A34A' }}>
                          View cleaned dataset →
                        </Button>
                        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                          This session only ·{' '}
                          <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>
                            Sign up free
                          </span>{' '}
                          to save permanently
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}
            </Box>
          </Box>
        )}

        {/* ══ FILTER ══ */}
        {tab === 'filter' && (() => {
          // Per-dataset derived values (computed inline — datasets are small)
          const fCols: string[] = filterDataset === 'sales' ? (columns as string[])
            : filterDataset === 'regional' ? Object.keys(regionTargets[0])
            : Object.keys(productCatalog[0]);
          const fBaseRows: any[] = filterDataset === 'sales' ? allData
            : filterDataset === 'regional' ? regionTargets : productCatalog;
          const fActive = filtersByDS[filterDataset] ?? [];
          const fResult: any[] = fBaseRows.filter(row => {
            for (const f of fActive) {
              const v  = String((row as any)[f.col]).toLowerCase();
              const fv = f.val.toLowerCase();
              if (f.op === 'is'               && v !== fv)                                  return false;
              if (f.op === 'contains'         && !v.includes(fv))                           return false;
              if (f.op === 'is greater than'  && !(parseFloat(v) > parseFloat(fv)))         return false;
              if (f.op === 'is less than'     && !(parseFloat(v) < parseFloat(fv)))         return false;
            }
            return true;
          });
          const fSaved = !!filterSavedMap[filterDataset];
          const fLabel = filterDataset === 'sales' ? 'sales_2024.csv'
            : filterDataset === 'regional' ? 'Regional Targets' : 'Product Catalog';
          const fSavedLabel = `${fLabel} — Filtered`;

          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* Excel-style dataset tab strip */}
            <Box sx={{ flexShrink: 0, bgcolor: '#E5E7EB', borderBottom: '1px solid #9CA3AF' }}>
            <Box sx={{
              overflowX: 'auto', overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin' as any,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: '#E5E7EB' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#9CA3AF', borderRadius: 2 },
              display: 'flex', alignItems: 'flex-end',
              px: 1.5, pt: 1,
            }}>
              {(['sales','regional','products'] as DS[])
                .filter(k => tabDatasets.some(t => (t in DS_META) && DS_META[t as TabDS].baseDS === k))
                .map(key => {
                  const isActive = filterDataset === key;
                  const isSaved  = !!filterSavedMap[key];
                  const dsLabel  = DS_META[key].label;
                  return (
                    <Box key={key}
                      onClick={() => {
                        if (isActive) return;
                        setFilterDataset(key);
                        setFilterCol(key === 'sales' ? 'region' : key === 'regional' ? 'region' : 'product');
                        setFilterOp('is');
                        setFilterVal('');
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 2, py: 0.75, mr: '2px',
                        cursor: isActive ? 'default' : 'pointer',
                        whiteSpace: 'nowrap', userSelect: 'none',
                        borderRadius: '5px 5px 0 0', border: '1px solid',
                        borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                        borderBottom: isActive ? '1px solid #fff' : '1px solid #C9CDD4',
                        bgcolor: isActive ? '#fff' : '#D1D5DB',
                        mb: '-1px', transition: 'background-color 0.15s',
                        '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                      }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563' }}>
                        {dsLabel}
                      </Typography>
                      {isSaved && (
                        <Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: isActive ? '#DBEAFE' : '#C8E6C9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <EditIcon sx={{ fontSize: 8, color: isActive ? '#1D4ED8' : '#16A34A' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111', mb: 0.25 }}>Filter Rows</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2.5 }}>
                Show only the rows from <strong>{fLabel}</strong> that match your conditions.
              </Typography>

              {/* Builder */}
              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 }, bgcolor: '#fff', mb: 2.5 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 2 }}>Show rows where…</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Select size="small" value={fCols.includes(filterCol) ? filterCol : fCols[0]}
                    onChange={e => setFilterCol(e.target.value)}
                    sx={{ minWidth: 150, fontSize: '0.85rem', borderRadius: '8px' }}>
                    {fCols.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                  <Select size="small" value={filterOp} onChange={e => setFilterOp(e.target.value)}
                    sx={{ minWidth: 160, fontSize: '0.875rem', borderRadius: '8px' }}>
                    {['is','contains','is greater than','is less than'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                  <TextField size="small" value={filterVal} onChange={e => setFilterVal(e.target.value)} placeholder="Value…"
                    sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem' } }} />
                  <Button variant="contained"
                    onClick={() => {
                      const col = fCols.includes(filterCol) ? filterCol : fCols[0];
                      setFiltersByDS(m => ({ ...m, [filterDataset]: [...(m[filterDataset] ?? []), { col, op: filterOp, val: filterVal }] }));
                      setFilterSavedMap(m => ({ ...m, [filterDataset]: false }));
                    }}
                    sx={{ bgcolor: '#111', color: '#fff', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#333' } }}>
                    Add Filter
                  </Button>
                </Box>
              </Box>

              {/* Active filter chips */}
              {fActive.length > 0 && (
                <Box sx={{ mb: 2.5, p: 2.5, border: '1px solid #DBEAFE', borderRadius: '12px', bgcolor: '#EFF6FF' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1D4ED8', mb: 1.5 }}>ACTIVE FILTERS</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {fActive.map((f, i) => (
                      <Chip key={i}
                        label={`${f.col.replace(/_/g,' ')} ${f.op} "${f.val}"`}
                        onDelete={() => {
                          setFiltersByDS(m => ({ ...m, [filterDataset]: (m[filterDataset] ?? []).filter((_,j) => j !== i) }));
                          setFilterSavedMap(m => ({ ...m, [filterDataset]: false }));
                        }}
                        sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 600, fontSize: '0.78rem' }} />
                    ))}
                    <Button size="small"
                      onClick={() => { setFiltersByDS(m => ({ ...m, [filterDataset]: [] })); setFilterSavedMap(m => ({ ...m, [filterDataset]: false })); }}
                      sx={{ color: '#EF4444', fontSize: '0.78rem', textTransform: 'none', fontWeight: 600 }}>Clear all</Button>
                  </Box>
                </Box>
              )}

              {/* Preview table */}
              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden', mb: 2.5 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 600 }}>
                    {fResult.length.toLocaleString()} of {fBaseRows.length.toLocaleString()} rows match
                  </Typography>
                  {fActive.length === 0 && (
                    <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Add a filter above to narrow results</Typography>
                  )}
                </Box>
                <Box sx={{ overflow: 'auto', maxHeight: 340 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${fCols.length}, minmax(120px,1fr))`, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    {fCols.map(col => (
                      <Box key={col} sx={{ px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280' }}>{col.replace(/_/g,' ')}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {fResult.slice(0, 15).map((row, i) => (
                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${fCols.length}, minmax(120px,1fr))`, borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#F9FAFB' } }}>
                      {fCols.map(col => (
                        <Box key={col} sx={{ px: 2, py: 1 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatCell((row as any)[col], detectColumnType(col, fBaseRows.map((r: any) => r[col])))}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Save card — same pattern as Clean + Join */}
              <Box sx={{
                border: `1px solid ${fSaved ? '#86EFAC' : '#E5E7EB'}`,
                borderRadius: '12px', p: 3,
                bgcolor: fSaved ? '#F0FDF4' : '#fff',
                transition: 'all 0.2s',
              }}>
                {!fSaved ? (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', mb: 0.5 }}>Save as a filtered copy</Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                      Your original <strong>{fLabel}</strong> stays untouched. A new copy called{' '}
                      <strong>"{fSavedLabel}"</strong> will be saved with {fResult.length.toLocaleString()} rows
                      {fActive.length > 0 ? ` (${fActive.length} filter${fActive.length > 1 ? 's' : ''} applied)` : ''}.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="contained" startIcon={<EditIcon />}
                        disabled={fActive.length === 0}
                        onClick={() => {
                          const filterId = `filtered_${Date.now()}`;
                          const newEntry: SavedJoin = {
                            id: filterId,
                            label: fSavedLabel,
                            rows: fResult,
                            cols: fCols,
                          };
                          setSavedJoins(prev => [...prev, newEntry]);
                          setTabDatasets(prev => [...prev, filterId]);
                          setFilterSavedMap(m => ({ ...m, [filterDataset]: true }));
                          setActiveTab(filterId);
                          setTab('explore');
                        }}
                        sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
                          '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                        Save "{fSavedLabel}"
                      </Button>
                      {fActive.length === 0 && (
                        <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF' }}>Add at least one filter to save</Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#16A34A' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#16A34A' }}>Saved — {fSavedLabel}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{fResult.length.toLocaleString()} rows · {fActive.length} filter{fActive.length !== 1 ? 's' : ''} applied · available for this session</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #D1FAE5', borderRadius: '8px', p: 2, mb: 2 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Filters applied</Typography>
                      {fActive.map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <CheckIcon sx={{ fontSize: 13, color: '#16A34A' }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                            <strong style={{ fontFamily: 'monospace' }}>{f.col}</strong> {f.op} <strong>"{f.val}"</strong>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                        This session only ·{' '}
                        <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span>{' '}
                        to save permanently
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          );
        })()}

        {/* ══ JOIN ══ */}
        {tab === 'join' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* ── Radio + single tab row picker ── */}
            <Box sx={{ flexShrink: 0, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', px: { xs: 1.5, md: 3 }, pt: 1.5, pb: 0 }}>

              {/* Radio buttons row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
                  Assign to:
                </Typography>

                {/* LEFT radio pill */}
                <Box onClick={() => setJoinRadio('left')} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6,
                  borderRadius: '9999px', border: '1.5px solid',
                  borderColor: joinRadio === 'left' ? '#3B82F6' : '#E5E7EB',
                  bgcolor: joinRadio === 'left' ? '#EFF6FF' : '#fff',
                  cursor: 'pointer', userSelect: 'none',
                  transition: 'all 0.15s',
                }}>
                  {/* Radio dot */}
                  <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: '1.5px solid', flexShrink: 0,
                    borderColor: joinRadio === 'left' ? '#3B82F6' : '#D1D5DB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {joinRadio === 'left' && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3B82F6' }} />}
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: joinRadio === 'left' ? '#1D4ED8' : '#6B7280' }}>LEFT</Typography>
                  <Box sx={{ px: 0.875, py: 0.2, bgcolor: joinRadio === 'left' ? '#1D4ED8' : '#D1D5DB', borderRadius: '4px', transition: 'background-color 0.15s' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: joinRadio === 'left' ? '#fff' : '#6B7280', whiteSpace: 'nowrap' }}>
                      {DS_META[joinLeft].label}
                    </Typography>
                  </Box>
                </Box>

                {/* Join key / connector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MergeTypeIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                  {joinKey
                    ? <Box sx={{ px: 0.875, py: 0.25, bgcolor: '#F0FDF4', borderRadius: '4px', border: '1px solid #86EFAC' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#15803D', fontWeight: 700, fontFamily: 'monospace' }}>🔑 {joinKey}</Typography>
                      </Box>
                    : joinLeft === joinRight
                      ? <Typography sx={{ fontSize: '0.68rem', color: '#EF4444', fontWeight: 600 }}>same dataset — pick two different ones</Typography>
                      : <Typography sx={{ fontSize: '0.68rem', color: '#F59E0B', fontWeight: 600 }}>⚠ no shared column</Typography>
                  }
                </Box>

                {/* RIGHT radio pill */}
                <Box onClick={() => setJoinRadio('right')} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6,
                  borderRadius: '9999px', border: '1.5px solid',
                  borderColor: joinRadio === 'right' ? '#7C3AED' : '#E5E7EB',
                  bgcolor: joinRadio === 'right' ? '#F5F3FF' : '#fff',
                  cursor: 'pointer', userSelect: 'none',
                  transition: 'all 0.15s',
                }}>
                  <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: '1.5px solid', flexShrink: 0,
                    borderColor: joinRadio === 'right' ? '#7C3AED' : '#D1D5DB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {joinRadio === 'right' && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#7C3AED' }} />}
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: joinRadio === 'right' ? '#6D28D9' : '#6B7280' }}>RIGHT</Typography>
                  <Box sx={{ px: 0.875, py: 0.2, bgcolor: joinRadio === 'right' ? '#7C3AED' : '#D1D5DB', borderRadius: '4px', transition: 'background-color 0.15s' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: joinRadio === 'right' ? '#fff' : '#6B7280', whiteSpace: 'nowrap' }}>
                      {DS_META[joinRight].label}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Single row of dataset tabs */}
              <Box sx={{
                display: 'flex', alignItems: 'flex-end',
                bgcolor: '#E5E7EB', borderRadius: '6px 6px 0 0',
                px: 0.75, pt: 0.75,
                overflowX: 'auto', overflowY: 'hidden',
                msOverflowStyle: 'none' as any, scrollbarWidth: 'none' as any,
                WebkitOverflowScrolling: 'touch',
              }}>
                {tabDatasets.filter(ds => ds in DS_META).map(ds => {
                  const isLeft    = joinLeft  === ds;
                  const isRight   = joinRight === ds;
                  const isActive  = (joinRadio === 'left' && isLeft) || (joinRadio === 'right' && isRight);

                  return (
                    <Box key={ds}
                      onClick={() => {
                        if (joinRadio === 'left') {
                          if (ds !== joinLeft) { setJoinLeft(ds as TabDS); setJoinDone(false); setJoinSaved(false); setJoinPage(0); }
                        } else {
                          if (ds !== joinRight) { setJoinRight(ds as TabDS); setJoinDone(false); setJoinSaved(false); setJoinPage(0); }
                        }
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 1.75, py: 0.6, mr: '2px', whiteSpace: 'nowrap', userSelect: 'none',
                        borderRadius: '5px 5px 0 0', border: '1px solid',
                        borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                        borderBottom: isActive ? '1px solid #F9FAFB' : '1px solid #C9CDD4',
                        bgcolor: isActive ? '#F9FAFB' : '#D1D5DB',
                        mb: '-1px', cursor: isActive ? 'default' : 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                      }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563' }}>
                        {getTabLabel(ds)}
                      </Typography>
                      {/* Coloured dot(s) showing L/R assignment */}
                      {isLeft && (
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#3B82F6', flexShrink: 0 }} />
                      )}
                      {isRight && (
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#7C3AED', flexShrink: 0 }} />
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Legend */}
              <Box sx={{ display: 'flex', gap: 2, pt: 0.75, pb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#3B82F6' }} />
                  <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>= LEFT</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#7C3AED' }} />
                  <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>= RIGHT</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF' }}>Click a tab while a radio is selected to assign it</Typography>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>

            {/* Can't join warning */}
            {!joinKey && joinLeft !== joinRight && (
              <Box sx={{ p: 2.5, border: '1px solid #FEF3C7', bgcolor: '#FFFBEB', borderRadius: '12px', mb: 3 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400E' }}>No shared column between {DS_META[joinLeft].label} and {DS_META[joinRight].label}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#78350F', mt: 0.5 }}>Try a different combination — e.g. sales ↔ products (shared: product) or sales ↔ regional targets (shared: region).</Typography>
              </Box>
            )}

            {/* Setup — pick and run */}
            {!joinDone && joinKey && (
              <>
                {/* Summary strip */}
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, bgcolor: '#fff', mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ px: 1.25, py: 0.5, bgcolor: '#EFF6FF', borderRadius: '6px' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1D4ED8' }}>{DS_META[joinLeft].label}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>{getDSRows(joinLeft).length.toLocaleString()} rows</Typography>
                    </Box>
                    <MergeTypeIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                    <Box sx={{ px: 1.25, py: 0.5, bgcolor: '#F5F3FF', borderRadius: '6px' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#7C3AED' }}>{DS_META[joinRight].label}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>{getDSRows(joinRight).length.toLocaleString()} rows</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {getDSCols(joinRight).filter(c => c !== joinKey).map(c => (
                      <Chip key={c} label={c.replace(/_/g,' ')} size="small" sx={{ bgcolor: '#F3F4F6', color: '#374151', fontSize: '0.68rem', height: 20 }} />
                    ))}
                  </Box>
                  <Box sx={{ ml: { md: 'auto' }, p: 1, bgcolor: '#F0FDF4', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#15803D', fontWeight: 600 }}>Join key:</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#15803D', fontFamily: 'monospace' }}>{joinKey}</Typography>
                  </Box>
                </Box>

                {/* Preview of right table */}
                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden', mb: 3 }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #F3F4F6', bgcolor: '#F9FAFB' }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151' }}>Preview — {DS_META[joinRight].label}</Typography>
                  </Box>
                  <Box sx={{ overflow: 'auto' }}>
                    {(() => {
                      const rows = getDSRows(joinRight);
                      const cols = getDSCols(joinRight);
                      return (
                        <>
                          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, minmax(130px,1fr))`, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                            {cols.map(c => (
                              <Box key={c} sx={{ px: 2, py: 1 }}>
                                <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c === joinKey ? '#15803D' : '#6B7280' }}>
                                  {c.replace(/_/g,' ')}{c === joinKey ? ' 🔑' : ''}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          {rows.map((row: any, i: number) => (
                            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, minmax(130px,1fr))`, borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#F9FAFB' } }}>
                              {cols.map(c => (
                                <Box key={c} sx={{ px: 2, py: 1 }}>
                                  <Typography sx={{ fontSize: '0.8rem', color: '#374151', whiteSpace: 'nowrap' }}>{String(row[c])}</Typography>
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </>
                      );
                    })()}
                  </Box>
                </Box>

                <Tooltip title="Left Join keeps ALL rows from the left table intact — no data ever gets dropped. It then pulls in matching columns from the right table. If no match is found, those columns show blank. Safest join for enriching data." placement="top" arrow>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mb: 2, cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 0.5, borderBottom: '1px dashed #9CA3AF' }}>
                    Why Left Join?
                  </Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={() => { setJoinDone(true); setJoinPage(0); }}
                    startIcon={<MergeTypeIcon />}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4, py: 1.25, '&:hover': { bgcolor: '#333' } }}>
                    Run Left Join
                  </Button>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                    Adds {getDSCols(joinRight).filter(c => c !== joinKey).length} new columns to {getDSRows(joinLeft).length.toLocaleString()} rows
                  </Typography>
                </Box>
              </>
            )}

            {/* Joined result */}
            {joinDone && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ p: 1.5, bgcolor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#15803D', fontWeight: 700 }}>
                      ✓ Join complete — {joinedData.length.toLocaleString()} rows · {Object.keys(joinedData[0] || {}).length} columns
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => { setJoinDone(false); setJoinSaved(false); setJoinPage(0); }}
                    sx={{ fontSize: '0.8rem', textTransform: 'none', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '8px', px: 2 }}>
                    ← Change datasets
                  </Button>
                </Box>

                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden', mb: 1 }}>
                  {(() => {
                    const cols = Object.keys(joinedData[0] || {});
                    const origCols = getDSCols(joinLeft);
                    const newCols  = cols.filter(c => !origCols.includes(c));
                    return (
                      <Box sx={{ overflow: 'auto' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, minmax(130px,1fr))`, bgcolor: '#F9FAFB', borderBottom: '2px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 1 }}>
                          {cols.map(c => (
                            <Box key={c} sx={{ px: 2, py: 1.25, bgcolor: newCols.includes(c) ? '#F0FDF4' : undefined }}>
                              <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: newCols.includes(c) ? '#15803D' : '#6B7280', whiteSpace: 'nowrap' }}>
                                {c.replace(/_/g,' ')}{newCols.includes(c) ? ' ✦' : ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        {joinPageData.map((row, i) => (
                          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols.length}, minmax(130px,1fr))`, borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#F9FAFB' } }}>
                            {cols.map(c => (
                              <Box key={c} sx={{ px: 2, py: 1, bgcolor: newCols.includes(c) ? '#FAFFF9' : undefined }}>
                                <Typography sx={{ fontSize: '0.8rem', color: newCols.includes(c) ? '#166534' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {String(row[c])}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Button size="small" disabled={joinPage === 0} onClick={() => setJoinPage(p => p-1)} sx={{ fontSize: '0.75rem', minWidth: 'auto' }}>← Prev</Button>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>Page {joinPage+1} / {joinTotalPages}</Typography>
                  <Button size="small" disabled={joinPage >= joinTotalPages-1} onClick={() => setJoinPage(p => p+1)} sx={{ fontSize: '0.75rem', minWidth: 'auto' }}>Next →</Button>
                  <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                    Showing {joinPage*JOIN_PAGE_SIZE+1}–{Math.min((joinPage+1)*JOIN_PAGE_SIZE, joinedData.length)} of {joinedData.length.toLocaleString()} rows · ✦ = joined columns
                  </Typography>
                </Box>

                {/* ── Save joined table card ── */}
                {(() => {
                  const joinedName = `${DS_META[joinLeft].label} + ${DS_META[joinRight].label}`;
                  const joinedCols = Object.keys(joinedData[0] || {}).length;
                  const joinedRows = joinedData.length;
                  return (
                    <Box sx={{
                      border: `1px solid ${joinSaved ? '#86EFAC' : '#E5E7EB'}`,
                      borderRadius: '12px', p: 3,
                      bgcolor: joinSaved ? '#F0FDF4' : '#fff',
                      mt: 1, transition: 'all 0.2s',
                    }}>
                      {!joinSaved ? (
                        <>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', mb: 0.5 }}>Save as a joined table</Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                            Your original datasets stay untouched. A new copy called{' '}
                            <strong>"{joinedName}"</strong> will be saved with {joinedCols} columns across {joinedRows.toLocaleString()} rows, merged on{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#F0FDF4', px: 0.5, borderRadius: '3px', color: '#15803D' }}>{joinKey}</Box>.
                          </Typography>
                          <Button variant="contained" startIcon={<MergeTypeIcon />}
                            onClick={() => {
                              const joinId = `joined_${Date.now()}`;
                              const newJoin: SavedJoin = {
                                id: joinId,
                                label: joinedName,
                                rows: joinedData,
                                cols: Object.keys(joinedData[0] || {}),
                              };
                              setSavedJoins(prev => [...prev, newJoin]);
                              setTabDatasets(prev => [...prev, joinId]);
                              setActiveTab(joinId);
                              setTab('explore');
                              setJoinSaved(true);
                            }}
                            sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
                              '&:hover': { bgcolor: '#333' } }}>
                            Save "{joinedName}"
                          </Button>
                        </>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#16A34A' }} />
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#16A34A' }}>Saved — {joinedName}</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{joinedCols} columns · {joinedRows.toLocaleString()} rows · available for this session</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ bgcolor: '#fff', border: '1px solid #D1FAE5', borderRadius: '8px', p: 2, mb: 2 }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>What was joined</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ px: 1.25, py: 0.5, bgcolor: '#EFF6FF', borderRadius: '6px' }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1D4ED8' }}>{DS_META[joinLeft].label}</Typography>
                              </Box>
                              <MergeTypeIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                              <Box sx={{ px: 1.25, py: 0.5, bgcolor: '#F5F3FF', borderRadius: '6px' }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#7C3AED' }}>{DS_META[joinRight].label}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>on</Typography>
                              <Box sx={{ px: 0.875, py: 0.25, bgcolor: '#F0FDF4', borderRadius: '4px', border: '1px solid #86EFAC' }}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#15803D', fontWeight: 700, fontFamily: 'monospace' }}>🔑 {joinKey}</Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                              This session only ·{' '}
                              <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>
                                Sign up free
                              </span>{' '}
                              to save permanently
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })()}
              </>
            )}
            </Box>
          </Box>
        )}

        {/* ══ ADD COLUMN ══ */}
        {tab === 'add' && (() => {
          const addDSCols: string[] = addDataset === 'sales' ? (columns as string[])
            : addDataset === 'regional' ? Object.keys(regionTargets[0])
            : Object.keys(productCatalog[0]);
          const addDSBaseRows: any[] = addDataset === 'sales' ? allData
            : addDataset === 'regional' ? regionTargets : productCatalog;
          const addDSLabel = addDataset === 'sales' ? 'sales_2024.csv'
            : addDataset === 'regional' ? 'Regional Targets' : 'Product Catalog';
          // Determine numeric / categorical cols dynamically for non-sales datasets
          const addAllCols = addDataset === 'sales'
            ? ([...NUM_COLS, ...CAT_COLS] as string[])
            : addDSCols;
          const addSaved = !!addSavedMap[addDataset];
          const addSavedLabel = `${addDSLabel} — Extended`;

          // Compute result rows: base rows + all added label columns
          const computeLabel = (row: any, l: typeof addedLabels[0]) => {
            const v = (row as any)[l.col];
            const num = Number(v);
            let match = false;
            if (l.op === 'is greater than')  match = num > Number(l.thresh);
            else if (l.op === 'is less than') match = num < Number(l.thresh);
            else if (l.op === 'is equal to')  match = String(v).toLowerCase() === l.thresh.toLowerCase();
            else if (l.op === 'contains')     match = String(v).toLowerCase().includes(l.thresh.toLowerCase());
            return match ? l.t : l.f;
          };
          const addResultRows = addDSBaseRows.map(row => {
            const extra: Record<string, string> = {};
            for (const l of addedLabels) extra[l.name] = computeLabel(row, l);
            return { ...row, ...extra };
          });
          const addResultCols = [...addDSCols, ...addedLabels.map(l => l.name)];

          // % preview count
          const previewMatch = labelColName ? addDSBaseRows.filter(row => {
            const v = (row as any)[labelWhenCol];
            const num = Number(v);
            if (labelCondition === 'is greater than')  return num > Number(labelThreshold);
            if (labelCondition === 'is less than')     return num < Number(labelThreshold);
            if (labelCondition === 'is equal to')      return String(v).toLowerCase() === labelThreshold.toLowerCase();
            if (labelCondition === 'contains')         return String(v).toLowerCase().includes(labelThreshold.toLowerCase());
            return false;
          }).length : 0;

          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* Excel-style dataset tab strip */}
            <Box sx={{ flexShrink: 0, bgcolor: '#E5E7EB', borderBottom: '1px solid #9CA3AF' }}>
            <Box sx={{
              overflowX: 'auto', overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin' as any,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: '#E5E7EB' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#9CA3AF', borderRadius: 2 },
              display: 'flex', alignItems: 'flex-end',
              px: 1.5, pt: 1,
            }}>
              {(['sales','regional','products'] as DS[])
                .filter(k => tabDatasets.some(t => (t in DS_META) && DS_META[t as TabDS].baseDS === k))
                .map(key => {
                  const isActive = addDataset === key;
                  const isSaved  = !!addSavedMap[key];
                  return (
                    <Box key={key}
                      onClick={() => {
                        if (isActive) return;
                        setAddDataset(key);
                        setAddedLabels([]);
                        setLabelColName('');
                        setAddSavedMap(m => ({ ...m, [key]: false }));
                        // reset column picker to first col of new dataset
                        const firstCol = key === 'sales' ? 'sales'
                          : key === 'regional' ? 'sales_target_2024' : 'avg_rating';
                        setLabelWhenCol(firstCol);
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 2, py: 0.75, mr: '2px',
                        cursor: isActive ? 'default' : 'pointer',
                        whiteSpace: 'nowrap', userSelect: 'none',
                        borderRadius: '5px 5px 0 0', border: '1px solid',
                        borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                        borderBottom: isActive ? '1px solid #fff' : '1px solid #C9CDD4',
                        bgcolor: isActive ? '#fff' : '#D1D5DB',
                        mb: '-1px', transition: 'background-color 0.15s',
                        '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                      }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563' }}>
                        {DS_META[key].label}
                      </Typography>
                      {isSaved && (
                        <Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: isActive ? '#EDE9FE' : '#C8E6C9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <AddCircleOutlineIcon sx={{ fontSize: 9, color: isActive ? '#7C3AED' : '#16A34A' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111', mb: 0.25 }}>Add a Label Column</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2.5, lineHeight: 1.5 }}>
                Tag each row in <strong>{addDSLabel}</strong> with a label based on a simple rule — no coding needed.
              </Typography>

              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 }, bgcolor: '#fff', mb: 2.5, maxWidth: 700 }}>

                {/* Step 1 */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#111', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Step 1</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 1.5 }}>What should this new column be called?</Typography>
                  <TextField size="small" value={labelColName} onChange={e => setLabelColName(e.target.value)}
                    placeholder="e.g. Revenue Tier, High Value Flag" fullWidth
                    sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' } }} />
                </Box>

                {/* Step 2 */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#111', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Step 2</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 1.5 }}>Tag each row based on this rule:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>When</Typography>
                    <Select size="small" value={addAllCols.includes(labelWhenCol) ? labelWhenCol : addAllCols[0]}
                      onChange={e => setLabelWhenCol(e.target.value)}
                      sx={{ minWidth: 140, fontSize: '0.85rem', borderRadius: '8px' }}>
                      {addAllCols.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g,' ')}</MenuItem>)}
                    </Select>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                    <Select size="small" value={labelCondition} onChange={e => setLabelCondition(e.target.value)}
                      sx={{ minWidth: 160, fontSize: '0.85rem', borderRadius: '8px' }}>
                      {['is greater than','is less than','is equal to','contains'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </Select>
                    <TextField size="small" value={labelThreshold} onChange={e => setLabelThreshold(e.target.value)} placeholder="value"
                      sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' } }} />
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#111', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Step 3</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 1.5 }}>What labels to apply?</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mb: 0.5 }}>If rule is TRUE, label as:</Typography>
                      <TextField size="small" value={labelIfTrue} onChange={e => setLabelIfTrue(e.target.value)} placeholder="e.g. High" fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' } }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mb: 0.5 }}>Otherwise, label as:</Typography>
                      <TextField size="small" value={labelIfFalse} onChange={e => setLabelIfFalse(e.target.value)} placeholder="e.g. Low" fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' } }} />
                    </Box>
                  </Box>
                </Box>

                {/* Inline preview */}
                {labelColName && (
                  <Box sx={{ p: 2, bgcolor: '#F5F3FF', borderRadius: '8px', mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#7C3AED', mb: 0.5 }}>PREVIEW</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                      A new column <strong>"{labelColName}"</strong> will label each row{' '}
                      <strong style={{ color: '#7C3AED' }}>"{labelIfTrue}"</strong> when{' '}
                      {(addAllCols.includes(labelWhenCol) ? labelWhenCol : addAllCols[0]).replace(/_/g,' ')} {labelCondition} {labelThreshold},
                      and <strong style={{ color: '#6B7280' }}>"{labelIfFalse}"</strong> otherwise.
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#7C3AED', mt: 1 }}>
                      ≈ {Math.round((previewMatch / Math.max(addDSBaseRows.length, 1)) * 100)}% of rows will be labelled "{labelIfTrue}"
                    </Typography>
                  </Box>
                )}

                <Button variant="contained" disabled={!labelColName.trim()}
                  onClick={() => {
                    const col = addAllCols.includes(labelWhenCol) ? labelWhenCol : addAllCols[0];
                    setAddedLabels(l => [...l, { name: labelColName.trim(), col, op: labelCondition, thresh: labelThreshold, t: labelIfTrue, f: labelIfFalse }]);
                    setLabelColName('');
                    setAddSavedMap(m => ({ ...m, [addDataset]: false }));
                  }}
                  sx={{ bgcolor: '#111', color: '#fff', borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                  Add Column
                </Button>
              </Box>

              {/* Added columns list */}
              {addedLabels.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1 }}>
                    ✦ Columns to add ({addedLabels.length})
                  </Typography>
                  {addedLabels.map((l, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid #E9D5FF', borderRadius: '8px', mb: 1, bgcolor: '#FAFAFE' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7C3AED', flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#7C3AED', minWidth: 100 }}>{l.name}</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', flex: 1 }}>
                        "{l.t}" when {l.col.replace(/_/g,' ')} {l.op} {l.thresh}, else "{l.f}"
                      </Typography>
                      <Button size="small" onClick={() => { setAddedLabels(ls => ls.filter((_,j)=>j!==i)); setAddSavedMap(m => ({ ...m, [addDataset]: false })); }}
                        sx={{ color: '#EF4444', fontSize: '0.75rem', textTransform: 'none', minWidth: 'auto' }}>Remove</Button>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Save card — same pattern as Clean / Join / Filter */}
              <Box sx={{
                border: `1px solid ${addSaved ? '#86EFAC' : '#E5E7EB'}`,
                borderRadius: '12px', p: 3,
                bgcolor: addSaved ? '#F0FDF4' : '#fff',
                transition: 'all 0.2s',
              }}>
                {!addSaved ? (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', mb: 0.5 }}>Save as an extended copy</Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                      Your original <strong>{addDSLabel}</strong> stays untouched. A new copy called{' '}
                      <strong>"{addSavedLabel}"</strong> will be saved with {addResultCols.length} columns
                      ({addedLabels.length} new label{addedLabels.length !== 1 ? 's' : ''} added).
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
                        disabled={addedLabels.length === 0}
                        onClick={() => {
                          const addId = `added_${Date.now()}`;
                          const newEntry: SavedJoin = {
                            id: addId,
                            label: addSavedLabel,
                            rows: addResultRows,
                            cols: addResultCols,
                          };
                          setSavedJoins(prev => [...prev, newEntry]);
                          setTabDatasets(prev => [...prev, addId]);
                          setAddSavedMap(m => ({ ...m, [addDataset]: true }));
                          setActiveTab(addId);
                          setTab('explore');
                        }}
                        sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
                          '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                        Save "{addSavedLabel}"
                      </Button>
                      {addedLabels.length === 0 && (
                        <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF' }}>Add at least one column to save</Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#16A34A' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#16A34A' }}>Saved — {addSavedLabel}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{addResultCols.length} columns · {addDSBaseRows.length.toLocaleString()} rows · available for this session</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #D1FAE5', borderRadius: '8px', p: 2, mb: 2 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Columns added</Typography>
                      {addedLabels.map((l, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <CheckIcon sx={{ fontSize: 13, color: '#16A34A' }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                            <strong style={{ fontFamily: 'monospace' }}>{l.name}</strong> — "{l.t}" when {l.col.replace(/_/g,' ')} {l.op} {l.thresh}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                      This session only ·{' '}
                      <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span>{' '}
                      to save permanently
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          );
        })()}

        {/* ══ CHART ══ */}
        {tab === 'chart' && (() => {
          const chartAllCols = getTabCols(chartDataset);
          const chartRows    = getTabRows(chartDataset);
          // Detect numeric cols for Y-axis (use known list for sales, detect for others)
          const chartNumCols = chartAllCols.filter(c => {
            const baseDS = (chartDataset in DS_META) ? DS_META[chartDataset as TabDS].baseDS : null;
            if (baseDS === 'sales') return (NUM_COLS as string[]).includes(c);
            const sampleVals = chartRows.slice(0, 10).map((r: any) => r[c]);
            const ct = detectColumnType(c, sampleVals);
            return ct === 'integer' || ct === 'decimal' || ct === 'currency' || ct === 'percent';
          });
          const yOptions = chartNumCols.length > 0 ? chartNumCols : chartAllCols;
          const chartSavedLabel = `Chart — ${getTabLabel(chartDataset)}`;

          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* Excel-style dataset tab strip */}
            <Box sx={{ flexShrink: 0, position: 'relative', bgcolor: '#E5E7EB', borderBottom: '1px solid #9CA3AF' }}>
              {tabDatasets.length > 3 && (
                <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 40, zIndex: 2,
                  background: 'linear-gradient(to right, transparent, #E5E7EB)', pointerEvents: 'none' }} />
              )}
              <Box sx={{
                overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin' as any,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: '#E5E7EB' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#9CA3AF', borderRadius: 2 },
                display: 'flex', alignItems: 'flex-end', px: 1.5, pt: 0.75,
              }}>
                {tabDatasets.map(ds => {
                  const isActive = chartDataset === ds;
                  return (
                    <Box key={ds}
                      onClick={() => { if (!isActive) { setChartDataset(ds); setChartSaved(false); } }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        pl: 1.5, pr: 0.75, py: 0.6, mr: '2px',
                        borderRadius: '5px 5px 0 0', border: '1px solid',
                        borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                        borderBottom: isActive ? '1px solid #fff' : '1px solid #C9CDD4',
                        bgcolor: isActive ? '#fff' : '#D1D5DB',
                        mb: '-1px', cursor: isActive ? 'default' : 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                      }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563', whiteSpace: 'nowrap', userSelect: 'none' }}>
                        {getTabLabel(ds)}
                      </Typography>
                      {isTabCleaned(ds) && (
                        <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                          <CheckIcon sx={{ fontSize: 8, color: '#16A34A' }} />
                        </Box>
                      )}
                      {isTabJoined(ds) && (
                        <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                          <MergeTypeIcon sx={{ fontSize: 8, color: '#3B82F6' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>

              {/* Controls — sticky so they stay visible while scrolling */}
              <Box sx={{
                border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 }, bgcolor: '#fff', mb: 3,
                position: 'sticky', top: 0, zIndex: 10,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              }}>
                <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {/* Chart type */}
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Chart Type</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(['bar','line','pie','scatter'] as const).map(ct => (
                        <Button key={ct} size="small" onClick={() => setChartType(ct)}
                          variant={chartType === ct ? 'contained' : 'outlined'}
                          sx={{ textTransform: 'capitalize', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', minWidth: 56,
                            ...(chartType === ct ? { bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#333' } } : { borderColor: '#E5E7EB', color: '#6B7280' }) }}>
                          {ct}
                        </Button>
                      ))}
                      {/* Orientation toggle — only for bar */}
                      {chartType === 'bar' && (
                        <Box sx={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', ml: 1 }}>
                          {(['vertical','horizontal'] as const).map(o => (
                            <Button key={o} size="small" onClick={() => setBarOrientation(o)}
                              sx={{ textTransform: 'capitalize', borderRadius: 0, fontWeight: 600, fontSize: '0.75rem', px: 1.5, minWidth: 0, border: 'none',
                                bgcolor: barOrientation === o ? '#F3F4F6' : 'transparent', color: barOrientation === o ? '#111' : '#9CA3AF',
                                '&:hover': { bgcolor: '#F9FAFB' } }}>
                              {o === 'vertical' ? '↕ Vertical' : '↔ Horizontal'}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* X Axis */}
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>X Axis (Category)</Typography>
                    <Select size="small"
                      value={chartAllCols.includes(chartData.effX) ? chartData.effX : (chartAllCols[0] ?? '')}
                      onChange={e => setXAxis(e.target.value)}
                      sx={{ minWidth: { xs: '100%', sm: 160 }, fontSize: '0.85rem', borderRadius: '8px' }}>
                      {chartAllCols.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g,' ')}</MenuItem>)}
                    </Select>
                  </Box>

                  {/* Y Axis */}
                  <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Y Axis (Measure)</Typography>
                    <Select size="small"
                      value={yOptions.includes(chartData.effY) ? chartData.effY : (yOptions[0] ?? '')}
                      onChange={e => setYAxis(e.target.value)}
                      sx={{ minWidth: { xs: '100%', sm: 160 }, fontSize: '0.85rem', borderRadius: '8px' }}>
                      {yOptions.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g,' ')}</MenuItem>)}
                    </Select>
                  </Box>

                  {/* Aggregation */}
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Aggregate By</Typography>
                    <Select size="small" value={aggFunc} onChange={e => setAggFunc(e.target.value as any)}
                      sx={{ minWidth: 130, fontSize: '0.875rem', borderRadius: '8px' }}>
                      {(['sum','count','average','max','min'] as const).map(a => <MenuItem key={a} value={a}>{a.charAt(0).toUpperCase()+a.slice(1)}</MenuItem>)}
                    </Select>
                  </Box>
                </Box>
              </Box>

              {/* Chart */}
              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', bgcolor: '#fff', p: 3, mb: 3 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                  {aggFunc.charAt(0).toUpperCase()+aggFunc.slice(1)} of {chartData.effY.replace(/_/g,' ')} by {chartData.effX.replace(/_/g,' ')}
                  {' · '}<span style={{ color: '#6B7280', fontWeight: 400 }}>{getTabLabel(chartDataset)}</span>
                </Typography>
                {(() => {
                  const BLUE = '#3B82F6';
                  const PALETTE = ['#3B82F6','#6366F1','#8B5CF6','#EC4899','#F59E0B','#10B981','#EF4444','#14B8A6','#F97316'];
                  const rc = chartData.labels.map((name, i) => ({ name, value: chartData.values[i], index: i }));
                  const isCurrency = /sales|revenue|price|cost|amount/.test(chartData.effY);
                  const isPct = /profit_margin|discount|rate|pct/.test(chartData.effY);
                  const fmtTick = (v: number) => isCurrency ? `$${(v/1000).toFixed(0)}k` : isPct ? `${(v*100).toFixed(0)}%` : v.toLocaleString();
                  const fmtTooltip = (v: number) => isCurrency ? `$${v.toLocaleString()}` : isPct ? `${(v*100).toFixed(1)}%` : v.toLocaleString();
                  const xLabel = chartData.effX.replace(/_/g,' ');
                  const yLabel = chartData.effY.replace(/_/g,' ');

                  if (chartType === 'bar' && barOrientation === 'vertical') return (
                    <ResponsiveContainer width="100%" height={380}>
                      <BarChart data={rc} margin={{ top: 16, right: 24, left: 16, bottom: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} angle={-35} textAnchor="end" interval={0} axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                          label={{ value: xLabel, position: 'insideBottom', offset: -60, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={fmtTick} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={60}
                          label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <RechartsTooltip formatter={(v: any) => [fmtTooltip(Number(v)), yLabel]} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                        <Bar dataKey="value" fill={BLUE} radius={[4, 4, 0, 0]} animationDuration={800} animationEasing="ease-out" />
                      </BarChart>
                    </ResponsiveContainer>
                  );

                  if (chartType === 'bar' && barOrientation === 'horizontal') return (
                    <ResponsiveContainer width="100%" height={Math.max(300, rc.length * 44 + 80)}>
                      <BarChart data={rc} layout="vertical" margin={{ top: 8, right: 70, left: 16, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={fmtTick} axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                          label={{ value: yLabel, position: 'insideBottom', offset: -28, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} width={100} axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                          label={{ value: xLabel, angle: -90, position: 'insideLeft', offset: 16, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <RechartsTooltip formatter={(v: any) => [fmtTooltip(Number(v)), yLabel]} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                        <Bar dataKey="value" fill={BLUE} radius={[0, 6, 6, 0]} animationDuration={800} animationEasing="ease-out">
                          <LabelList dataKey="value" position="right" formatter={(v: any) => fmtTooltip(Number(v))} style={{ fontSize: 11, fill: '#6B7280' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );

                  if (chartType === 'line') return (
                    <ResponsiveContainer width="100%" height={380}>
                      <LineChart data={rc} margin={{ top: 16, right: 24, left: 16, bottom: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} angle={-35} textAnchor="end" interval={0} axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                          label={{ value: xLabel, position: 'insideBottom', offset: -60, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={fmtTick} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={60}
                          label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <RechartsTooltip formatter={(v: any) => [fmtTooltip(Number(v)), yLabel]} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                        <Line type="monotone" dataKey="value" stroke={BLUE} strokeWidth={2.5} dot={{ fill: BLUE, r: 4 }} activeDot={{ r: 6 }} animationDuration={900} animationEasing="ease-out" />
                      </LineChart>
                    </ResponsiveContainer>
                  );

                  if (chartType === 'pie') return (
                    <Box>
                      {/* X / Y variable badges */}
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, bgcolor: '#F1F5F9', borderRadius: '999px', border: '1px solid #E5E7EB' }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category (X)</Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111' }}>{xLabel}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, bgcolor: '#EFF6FF', borderRadius: '999px', border: '1px solid #BFDBFE' }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value (Y)</Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1D4ED8' }}>{yLabel}</Typography>
                        </Box>
                      </Box>
                      <ResponsiveContainer width="100%" height={460}>
                        <PieChart margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                          <Pie data={rc} cx="50%" cy="42%" outerRadius="52%" innerRadius="26%" dataKey="value"
                            animationDuration={900} animationEasing="ease-out"
                            labelLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                            label={(props: any) => {
                              const { cx, cy, midAngle, outerRadius, name, value, percent } = props;
                              if ((percent ?? 0) < 0.03) return null; // skip tiny slices
                              const RADIAN = Math.PI / 180;
                              const radius = outerRadius + 28;
                              const x = cx + radius * Math.cos(-midAngle * RADIAN);
                              const y = cy + radius * Math.sin(-midAngle * RADIAN);
                              return (
                                <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 11, fontFamily: 'Inter, sans-serif' }}>
                                  <tspan fontWeight="600">{name}</tspan>
                                  <tspan x={x} dy="1.2em" fill="#6B7280">{fmtTooltip(value)}</tspan>
                                </text>
                              );
                            }}>
                            {rc.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                          </Pie>
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const d = payload[0].payload;
                              return (
                                <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', px: 1.75, py: 1.25, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, mb: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{xLabel}</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#111' }}>{d.name}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{yLabel}</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3B82F6' }}>{fmtTooltip(d.value)}</Typography>
                                  </Box>
                                </Box>
                              );
                            }}
                          />
                          <Legend
                            layout="horizontal" align="center" verticalAlign="bottom"
                            iconType="circle" iconSize={10}
                            formatter={(value, entry: any) => (
                              <span style={{ fontSize: 12, color: '#374151', marginRight: 8 }}>
                                {value}&nbsp;<span style={{ color: '#6B7280' }}>{fmtTooltip(entry.payload.value)}</span>
                              </span>
                            )}
                            wrapperStyle={{ paddingTop: 16, lineHeight: '26px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  );

                  // Scatter
                  const scatterData = rc.map((d, i) => ({ x: i, y: d.value, name: d.name }));
                  return (
                    <ResponsiveContainer width="100%" height={380}>
                      <ScatterChart margin={{ top: 16, right: 24, left: 16, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="x" type="number" tick={false} axisLine={{ stroke: '#E5E7EB' }} tickLine={false}
                          label={{ value: xLabel, position: 'insideBottom', offset: -12, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <YAxis dataKey="y" tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={fmtTick} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={60}
                          label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                        <RechartsTooltip cursor={{ strokeDasharray: '3 3' }}
                          content={({ active, payload }) => active && payload?.[0] ? (
                            <Box sx={{ bgcolor: '#111', color: '#fff', px: 1.5, py: 1, borderRadius: '8px', fontSize: 12 }}>
                              <div>{payload[0].payload.name}</div>
                              <div style={{ fontWeight: 700 }}>{fmtTooltip(payload[0].payload.y)}</div>
                            </Box>
                          ) : null} />
                        <Scatter data={scatterData} fill={BLUE} animationDuration={800} />
                      </ScatterChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Box>

              {/* ── Save chart data card ── */}
              <Box sx={{
                border: `1px solid ${chartSaved ? '#86EFAC' : '#E5E7EB'}`,
                borderRadius: '12px', p: 3,
                bgcolor: chartSaved ? '#F0FDF4' : '#fff',
                transition: 'all 0.2s',
              }}>
                {!chartSaved ? (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', mb: 0.5 }}>Save chart data as a dataset</Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mb: 2 }}>
                      Save the aggregated results as a new tab —{' '}
                      <strong>"{chartSavedLabel}"</strong> — with {chartData.labels.length} rows (one per group).
                      You can then explore, filter, or join it like any other dataset.
                    </Typography>
                    <Button variant="contained" startIcon={<BarChartIcon />}
                      onClick={() => {
                        const chartId = `chart_${Date.now()}`;
                        const aggLabel = `${aggFunc}_${chartData.effY}`;
                        const chartResultRows = chartData.labels.map((lbl, i) => ({
                          [chartData.effX]: lbl,
                          [aggLabel]: chartData.values[i],
                        }));
                        const newEntry: SavedJoin = {
                          id: chartId,
                          label: chartSavedLabel,
                          rows: chartResultRows,
                          cols: [chartData.effX, aggLabel],
                        };
                        setSavedJoins(prev => [...prev, newEntry]);
                        setTabDatasets(prev => [...prev, chartId]);
                        setChartSaved(true);
                        setActiveTab(chartId);
                        setTab('explore');
                      }}
                      sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
                        '&:hover': { bgcolor: '#333' } }}>
                      Save "{chartSavedLabel}"
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#16A34A' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#16A34A' }}>Saved — {chartSavedLabel}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{chartData.labels.length} rows · 2 columns · available for this session</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #D1FAE5', borderRadius: '8px', p: 2, mb: 2 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>What was saved</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>
                        <strong>{aggFunc.charAt(0).toUpperCase()+aggFunc.slice(1)}</strong> of{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#F0FDF4', px: 0.5, borderRadius: '3px', color: '#15803D' }}>{chartData.effY.replace(/_/g,' ')}</Box>{' '}
                        grouped by{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#EFF6FF', px: 0.5, borderRadius: '3px', color: '#1D4ED8' }}>{chartData.effX.replace(/_/g,' ')}</Box>{' '}
                        — {chartData.labels.length} groups from <strong>{getTabLabel(chartDataset)}</strong>
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                      This session only ·{' '}
                      <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span>{' '}
                      to save permanently
                    </Typography>
                  </>
                )}
              </Box>

              {/* ── Category Distribution ── */}
              {(() => {
                const rows = getTabRows(chartDataset);
                const cols = getTabCols(chartDataset);
                // Find categorical (non-numeric) columns
                const catCols = cols.filter(col => {
                  const vals = rows.slice(0, 20).map((r: any) => r[col]);
                  return vals.some(v => isNaN(Number(v)) && v !== null && v !== undefined && String(v).trim() !== '');
                });
                if (catCols.length === 0) return null;
                return (
                  <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', bgcolor: '#fff', p: 3, mb: 3 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111', mb: 0.5 }}>
                      Category Distribution
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mb: 3 }}>
                      View distribution of categorical data
                    </Typography>
                    {catCols.map(col => {
                      const counts: Record<string, number> = {};
                      rows.forEach((r: any) => {
                        const val = String(r[col] ?? '').trim();
                        if (val) counts[val] = (counts[val] || 0) + 1;
                      });
                      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
                      const maxCount = sorted[0]?.[1] || 1;
                      const COLORS = ['#3B82F6','#6366F1','#8B5CF6','#EC4899','#F59E0B','#10B981','#EF4444','#14B8A6','#F97316','#84CC16'];
                      return (
                        <Box key={col} sx={{ mb: 3 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', mb: 1.5, textTransform: 'capitalize' }}>
                            {col.replace(/_/g, ' ')}
                          </Typography>
                          {sorted.map(([val, count], i) => (
                            <Box key={val} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', minWidth: 110, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {val}
                              </Typography>
                              <Box sx={{ flex: 1, height: 24, bgcolor: '#F1F5F9', borderRadius: '999px', overflow: 'hidden' }}>
                                <Box sx={{
                                  height: '100%',
                                  width: `${Math.max((count / maxCount) * 100, 8)}%`,
                                  bgcolor: COLORS[i % COLORS.length],
                                  borderRadius: '999px',
                                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1.25,
                                  transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
                                }}>
                                  <Typography sx={{ fontSize: '0.7rem', color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                                    {count}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}

            </Box>
          </Box>
          );
        })()}

        {/* ══ AI ══ */}
        {tab === 'ai' && (() => {
          const aiSavedLabel = `AI Chat — ${getTabLabel(aiDataset)}`;
          // Build Q&A pairs from message history (skip the initial greeting at index 0)
          const qaPairs: { Question: string; Answer: string }[] = [];
          for (let i = 1; i < aiMessages.length - 1; i++) {
            if (aiMessages[i].role === 'user' && aiMessages[i + 1]?.role === 'ai') {
              qaPairs.push({ Question: aiMessages[i].text, Answer: aiMessages[i + 1].text });
            }
          }
          const hasMsgs = qaPairs.length > 0;

          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

            {/* Excel-style dataset tab strip */}
            <Box sx={{ flexShrink: 0, position: 'relative', bgcolor: '#E5E7EB', borderBottom: '1px solid #9CA3AF' }}>
              {tabDatasets.length > 3 && (
                <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 40, zIndex: 2,
                  background: 'linear-gradient(to right, transparent, #E5E7EB)', pointerEvents: 'none' }} />
              )}
              <Box sx={{
                overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin' as any,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: '#E5E7EB' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#9CA3AF', borderRadius: 2 },
                display: 'flex', alignItems: 'flex-end', px: 1.5, pt: 0.75,
              }}>
                {tabDatasets.map(ds => {
                  const isActive = aiDataset === ds;
                  return (
                    <Box key={ds}
                      onClick={() => { if (!isActive) { setAiDataset(ds); setAiSaved(false); } }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        pl: 1.5, pr: 0.75, py: 0.6, mr: '2px',
                        borderRadius: '5px 5px 0 0', border: '1px solid',
                        borderColor: isActive ? '#9CA3AF' : '#C9CDD4',
                        borderBottom: isActive ? '1px solid #fff' : '1px solid #C9CDD4',
                        bgcolor: isActive ? '#fff' : '#D1D5DB',
                        mb: '-1px', cursor: isActive ? 'default' : 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': !isActive ? { bgcolor: '#E9EAEC' } : {},
                      }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#111' : '#4B5563', whiteSpace: 'nowrap', userSelect: 'none' }}>
                        {getTabLabel(ds)}
                      </Typography>
                      {isTabCleaned(ds) && (
                        <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                          <CheckIcon sx={{ fontSize: 8, color: '#16A34A' }} />
                        </Box>
                      )}
                      {isTabJoined(ds) && (
                        <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 0.25 }}>
                          <MergeTypeIcon sx={{ fontSize: 8, color: '#3B82F6' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Chat area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: { xs: 1.5, md: 3 }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: '#7C3AED' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111' }}>Ask AI about your data</Typography>
                <Chip label={`Context: ${getTabLabel(aiDataset)}`} size="small"
                  sx={{ bgcolor: '#F5F3FF', color: '#7C3AED', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                <Chip label="Sample responses in demo" size="small" sx={{ bgcolor: '#F9FAFB', color: '#9CA3AF', fontWeight: 500, fontSize: '0.65rem', height: 20 }} />
              </Box>

              {/* Suggested prompts */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                {["What's the top-selling product?","Show revenue by region","What's the average profit margin?","Show me cumulative sales totals","Which month had the highest sales?","Are discounts helping sales?"].map(q => (
                  <Chip key={q} label={q} onClick={() => setAiInput(q)} size="small"
                    sx={{ bgcolor: '#F9FAFB', border: '1px solid #E5E7EB', color: '#374151', fontSize: '0.8rem', cursor: 'pointer', height: 28,
                      '&:hover': { bgcolor: '#EFF6FF', borderColor: '#BFDBFE', color: '#1D4ED8' } }} />
                ))}
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {aiMessages.map((msg, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{
                      maxWidth: '78%', px: 2.5, py: 1.75,
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                      bgcolor: msg.role === 'user' ? '#2563EB' : '#fff',
                      border: msg.role === 'ai' ? '1px solid #E5E7EB' : 'none',
                    }}>
                      {msg.role === 'ai' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <AutoAwesomeIcon sx={{ fontSize: 12, color: '#7C3AED' }} />
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#7C3AED', letterSpacing: '0.05em' }}>DATASCOPE AI</Typography>
                        </Box>
                      )}
                      <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', color: msg.role === 'user' ? '#fff' : '#111' }}>
                        {msg.text.replace(/\*\*(.*?)\*\*/g, '$1')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Input */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexShrink: 0 }}>
                <TextField fullWidth multiline maxRows={3} value={aiInput} onChange={e => setAiInput(e.target.value)}
                  placeholder="Ask anything about your data in plain English…"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAi(); } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.875rem', bgcolor: '#fff' } }} />
                <IconButton onClick={sendAi} disabled={!aiInput.trim()}
                  sx={{ bgcolor: '#111', color: '#fff', borderRadius: '10px', width: 44, height: 44, flexShrink: 0,
                    '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* ── Save conversation card ── */}
              <Box sx={{
                border: `1px solid ${aiSaved ? '#86EFAC' : '#E5E7EB'}`,
                borderRadius: '12px', p: 2.5, flexShrink: 0,
                bgcolor: aiSaved ? '#F0FDF4' : '#fff',
                transition: 'all 0.2s',
              }}>
                {!aiSaved ? (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#111', mb: 0.5 }}>Save conversation as a dataset</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mb: 1.5 }}>
                      Export this chat as a Q&amp;A table —{' '}
                      <strong>"{aiSavedLabel}"</strong> — with one row per question and answer.
                      {!hasMsgs && <span style={{ color: '#9CA3AF' }}> Ask at least one question to enable this.</span>}
                    </Typography>
                    <Button variant="contained" startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                      disabled={!hasMsgs}
                      onClick={() => {
                        const aiId = `ai_${Date.now()}`;
                        const newEntry: SavedJoin = {
                          id: aiId,
                          label: aiSavedLabel,
                          rows: qaPairs,
                          cols: ['Question', 'Answer'],
                        };
                        setSavedJoins(prev => [...prev, newEntry]);
                        setTabDatasets(prev => [...prev, aiId]);
                        setAiSaved(true);
                        setActiveTab(aiId);
                        setTab('explore');
                      }}
                      sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, fontSize: '0.82rem',
                        '&:hover': { bgcolor: '#333' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>
                      Save "{aiSavedLabel}"
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#16A34A' }}>Saved — {aiSavedLabel}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>{qaPairs.length} Q&amp;A pair{qaPairs.length !== 1 ? 's' : ''} · 2 columns · available for this session</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                      This session only ·{' '}
                      <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span>{' '}
                      to save permanently
                    </Typography>
                  </>
                )}
              </Box>

              <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center', flexShrink: 0 }}>
                Demo shows pre-computed responses ·{' '}
                <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp}>Sign up free</span>
                {' '}to connect real AI to your own data
              </Typography>
            </Box>
          </Box>
          );
        })()}

      </Box>
    </Box>
  );
};

export default DemoDatasetPage;
